import { cloneCombat, intentDropPiece, placePieceAt, previewCard, previewEnemyDrop, resolveBoardEnemy, type Combat, type CardDef, type CardInstance, type DeckCard, type PlayTarget, type PreviewWave, type TargetMode, type TurnView } from '../core/combat';
import { findMatches, findRuns } from '../core/match';
import { tierFor, tierGemMultiplier, BASE_GEM_VALUES } from '../core/effects';
import type { RelicDef } from '../core/relic';
import { canSocket, enchantById, type EnchantDef } from '../core/enchant';
import { socketCapacityFor, restOptions, type CacheOption, type RewardOption, type RunScore, type RunState } from '../game/run';
import { COMPONENTS, type ComponentDrop, type ComponentId } from '../game/components';
import { CRAFT_RECIPES, canCraft, missingFor } from '../game/crafting';
import { nodeById, nodeCenter, nodesFrom, visibleNodes, type MapNode, type NodeType } from '../game/map';
import { bossPreviewRect, regionLegendRect } from './layout';
import { present, type Presenter, type PresenterEnemy } from './presenter';
import { restRow, rotatePiece } from '../core/grid';
import { WELL_COLS, WELL_ROWS, GEM_KINDS, idxCol, idxRow, type GemKind, type Piece } from '../core/types';
import { composition } from '../core/gempool';
import type { Intent } from '../core/intent';
import { aimTarget, type Controller } from '../input/controller';
import { COMPONENT_COLOR, GEM_COLOR, GEM_GLYPH, PALETTE } from './palette';
import type { CharAnim, EnemyCharState, PlayerCharState } from './character';
import { drawEnemyChar, drawPlayerChar } from './character';
import { cellRect, contains, debugBoundsOn, mapGraphArea, purgeCardRects, restOptionRects, cacheOptionRects, craftOptionRects, craftBackRect, seedBadgeRect, socketCardRects, type Layout, type Rect } from './layout';
import { getMatchMin } from '../core/rules';
import { boardLabel, isDefaultBoard } from '../core/types';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const SANS = 'system-ui, -apple-system, "Segoe UI", sans-serif';

function roundedRect(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
): void {
  const { x, y, w, h } = r;
  const k = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.arcTo(x + w, y, x + w, y + h, k);
  ctx.arcTo(x + w, y + h, x, y + h, k);
  ctx.arcTo(x, y + h, x, y, k);
  ctx.arcTo(x, y, x + w, y, k);
  ctx.closePath();
}

function fillRound(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
  fill: string,
): void {
  roundedRect(ctx, r, radius);
  ctx.fillStyle = fill;
  ctx.fill();
}

function strokeRound(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
  stroke: string,
  width = 1,
): void {
  roundedRect(ctx, r, radius);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width;
  ctx.stroke();
}

function text(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  size: number,
  color: string = PALETTE.text,
  align: CanvasTextAlign = 'left',
  font = SANS,
): void {
  ctx.font = `${size}px ${font}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(s, x, y);
}

/**
 * Largest font size at or below `size` where `s` measures within `maxW`.
 * Rail content zones are narrow; text shrinks rather than spilling onto the
 * HP bar beside it.
 */
function fitSize(
  ctx: CanvasRenderingContext2D,
  s: string,
  size: number,
  maxW: number,
  font: string,
): number {
  if (maxW <= 0) return size;
  ctx.font = `${size}px ${font}`;
  const w = ctx.measureText(s).width;
  if (w <= maxW || w === 0) return size;
  return Math.max(6, Math.floor((size * maxW) / w));
}

function wrap(ctx: CanvasRenderingContext2D, s: string, maxW: number, size: number): string[] {
  ctx.font = `${size}px ${SANS}`;
  const out: string[] = [];
  for (const paragraph of s.split('\n')) {
    let line = '';
    for (const word of paragraph.split(' ')) {
      const probe = line ? `${line} ${word}` : word;
      if (ctx.measureText(probe).width > maxW && line) {
        out.push(line);
        line = word;
      } else {
        line = probe;
      }
    }
    out.push(line);
  }
  return out;
}

// --- well -----------------------------------------------------------------

function drawWell(ctx: CanvasRenderingContext2D, l: Layout): void {
  const lip = 6;
  fillRound(
    ctx,
    { x: l.well.x - lip, y: l.well.y - lip, w: l.well.w + lip * 2, h: l.well.h + lip * 2 },
    8,
    PALETTE.wellLip,
  );
  ctx.fillStyle = PALETTE.wellPit;
  ctx.fillRect(l.well.x, l.well.y, l.well.w, l.well.h);

  ctx.strokeStyle = 'rgba(255,255,255,0.04)';
  ctx.lineWidth = 1;
  for (let c = 1; c < WELL_COLS; c++) {
    ctx.beginPath();
    ctx.moveTo(l.wellX + c * l.cell, l.well.y);
    ctx.lineTo(l.wellX + c * l.cell, l.well.y + l.well.h);
    ctx.stroke();
  }
  for (let r = 1; r < WELL_ROWS; r++) {
    ctx.beginPath();
    ctx.moveTo(l.well.x, l.wellY + r * l.cell);
    ctx.lineTo(l.well.x + l.well.w, l.wellY + r * l.cell);
    ctx.stroke();
  }
}

/**
 * Bevelled square. Every gem is one such block — the unit a falling piece
 * is built from — so a dropped piece reads as a tetromino of blocks rather than
 * a row of unrelated icons. Identity rides on colour plus the inscribed glyph;
 * Stone is the matte, unlit block, so "inert" is legible at a glance.
 */
function drawGem(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  kind: GemKind,
  alpha = 1,
  tint?: string,
): void {
  // Stone is masonry, not a gem — it takes its own path everywhere it appears:
  // board, ghosts, falling pieces, card previews, intent previews.
  if (kind === 'stone' && tint === undefined) {
    drawStoneBrick(ctx, r);
    return;
  }

  const pad = Math.max(1, r.h * 0.06);
  const box = { x: r.x + pad, y: r.y + pad, w: r.w - pad * 2, h: r.h - pad * 2 };
  const body = tint ?? GEM_COLOR[kind];
  const unit = Math.min(box.w, box.h);
  const radius = Math.max(1, unit * 0.12);
  const lit = kind !== 'stone';

  ctx.globalAlpha = alpha;

  if (lit) {
    ctx.shadowColor = body;
    ctx.shadowBlur = r.h * 0.24;
  }
  fillRound(ctx, box, radius, body);
  ctx.shadowBlur = 0;

  ctx.save();
  roundedRect(ctx, box, radius);
  ctx.clip();

  // Raised face: a lighter inset panel, the classic block top.
  const inset = Math.max(1, unit * 0.16);
  fillRound(
    ctx,
    { x: box.x + inset, y: box.y + inset, w: box.w - inset * 2, h: box.h - inset * 2 },
    radius * 0.7,
    lit ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
  );

  // Bevel: lit top-left, shadowed bottom-right.
  const b = Math.max(1, unit * 0.07) / 2;
  ctx.lineWidth = b * 2;
  ctx.strokeStyle = lit ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.12)';
  ctx.beginPath();
  ctx.moveTo(box.x + b, box.y + box.h - b);
  ctx.lineTo(box.x + b, box.y + b);
  ctx.lineTo(box.x + box.w - b, box.y + b);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.moveTo(box.x + box.w - b, box.y + b);
  ctx.lineTo(box.x + box.w - b, box.y + box.h - b);
  ctx.lineTo(box.x + b, box.y + box.h - b);
  ctx.stroke();

  ctx.restore();

  // Inscribed glyph: identity that survives a small cell and a colourblind eye.
  const glyph = GEM_GLYPH[kind];
  if (box.w >= 14) {
    text(ctx, glyph, box.x + box.w / 2, box.y + box.h / 2 + 1, Math.round(box.h * 0.42), 'rgba(13,11,20,0.72)', 'center', MONO);
  }

  ctx.globalAlpha = 1;
}

/**
 * Ghost where each enemy's telegraphed piece will land. The drop AI is
 * deterministic given the board, so this is a promise, not a guess — the enemy
 * move becomes a square to plan around instead of a surprise.
 *
 * When the prediction changes because the player moved, the ghost slides to
 * its new destination rather than teleporting. That slide is the moment the
 * player learns "my move changed what the enemy wants to do", which is one of
 * the few interactions unique to sharing a well.
 */
const GHOST_MOVE_MS = 300;

interface GhostTrack {
  cells: number[];
  from: number[] | null;
  since: number;
}

const ghostTracks = new Map<number, GhostTrack>();

/**
 * Record this frame's prediction and report how far through a repositioning
 * animation we are. Returns k=1 when the ghost is settled, so the caller can
 * draw a still projection instead of a moving one.
 */
function trackGhost(uid: number, cells: number[]): { from: number[] | null; k: number } {
  const prev = ghostTracks.get(uid);
  const same =
    prev !== undefined &&
    prev.cells.length === cells.length &&
    prev.cells.every((c, i) => c === cells[i]);

  if (same && prev) {
    return { from: prev.from, k: Math.min(1, (performance.now() - prev.since) / GHOST_MOVE_MS) };
  }

  ghostTracks.set(uid, {
    cells: cells.slice(),
    from: prev ? prev.cells.slice() : null,
    since: performance.now(),
  });
  return { from: prev ? prev.cells.slice() : null, k: 0 };
}

/** Forget every tracked ghost. Called when a fight starts or ends. */
export function resetGhostTracks(): void {
  ghostTracks.clear();
}

function drawEnemyGhosts(ctx: CanvasRenderingContext2D, l: Layout, combat: Combat): void {
  const simulation = cloneCombat(combat);
  for (const enemy of simulation.enemies) {
    if (enemy.hp <= 0) continue;
    const piece = intentDropPiece(enemy.intent);
    if (!piece) continue;
    const placement = previewEnemyDrop(simulation, enemy);
    if (!placement) continue;

    const placed = rotatePiece(piece, placement.rotation);
    const row = restRow(simulation.grid, placed.cells, placement.column);
    if (row === null) continue;

    ctx.fillStyle = 'rgba(255,110,110,0.03)';
    ctx.fillRect(l.wellX + placement.column * l.cell, l.well.y, l.cell, l.well.h);

    const cells: number[] = [];
    const kinds: GemKind[] = [];
    for (let i = 0; i < placed.cells.length; i++) {
      const offset = placed.cells[i];
      const kind = placed.kinds[i];
      if (!offset || !kind) continue;
      const r = row + offset[0];
      const c = placement.column + offset[1];
      if (r < 0 || r >= WELL_ROWS || c < 0 || c >= WELL_COLS) continue;
      cells.push(r * WELL_COLS + c);
      kinds.push(kind);
    }
    if (cells.length === 0) continue;

    // Place the piece on the simulation WITHOUT resolving yet, then check
    // what runs it completes. Dangerous runs pulse — the board communicates
    // the consequence, not a paragraph on the intent panel.
    placePieceAt(simulation, piece, placement.column, placement.rotation);
    const ghostSet = new Set(cells);
    const runs = findRuns(simulation.grid);
    const dangerousRuns = runs.filter(run => run.cells.some(c => ghostSet.has(c)));

    const track = trackGhost(enemy.uid, cells);
    const moving = track.from !== null && track.k < 1;
    const alpha = moving ? 0.3 : 0.16;
    const edge = moving ? 'rgba(255,190,150,0.75)' : 'rgba(255,170,170,0.26)';
    const k = moving ? 1 - (1 - track.k) * (1 - track.k) : 1;

    // Draw ghost cells.
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      const kind = kinds[i];
      if (cell === undefined || !kind) continue;
      const to = cellRect(l, cell);
      let box = to;

      if (moving && track.from) {
        const fromIdx = track.from[i];
        if (fromIdx !== undefined) {
          const from = cellRect(l, fromIdx);
          box = {
            x: from.x + (to.x - from.x) * k,
            y: from.y + (to.y - from.y) * k,
            w: to.w,
            h: to.h,
          };
        }
      }

      drawGem(ctx, box, kind, alpha);
      ctx.save();
      ctx.strokeStyle = edge;
      ctx.lineWidth = moving ? 2 : 1;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(box.x + 2, box.y + 2, box.w - 4, box.h - 4);
      ctx.restore();
    }

    // Pulse cells that belong to a run this drop would complete. The board
    // itself says "this is dangerous" — no answer key on the intent panel.
    if (dangerousRuns.length > 0) {
      const pulse = 0.4 + 0.3 * Math.sin(performance.now() / 220);
      for (const run of dangerousRuns) {
        const tier = tierFor(run.cells.length);
        const mult = tierGemMultiplier(tier);
        const size = run.cells.length;

        // Pulse every cell in the threatened run.
        for (const cell of run.cells) {
          const r = cellRect(l, cell);
          ctx.save();
          ctx.globalAlpha = pulse;
          ctx.strokeStyle = GEM_COLOR[run.kind];
          ctx.lineWidth = 2;
          ctx.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
          ctx.restore();
        }

        // Compact consequence label near the run's first cell: icon + amount.
        const firstCell = run.cells[0];
        if (firstCell === undefined) continue;
        const fr = cellRect(l, firstCell);

        let label = '';
        let labelColor = GEM_COLOR[run.kind];
        if (run.kind === 'ember') {
          const dmg = size * BASE_GEM_VALUES.emberDamage * mult;
          label = `${dmg}`;
        } else if (run.kind === 'frost') {
          const blk = size * BASE_GEM_VALUES.frostBlock * mult;
          label = `+${blk}`;
        } else if (run.kind === 'venom') {
          const psn = size * BASE_GEM_VALUES.venomPoison * mult;
          label = `+${psn}`;
        } else if (run.kind === 'volt') {
          label = `${size}⚡`;
        }
        if (!label) continue;

        // Tier badge for Charged+.
        const tierBadge = tier !== 'base' ? tier[0]!.toUpperCase() : '';

        ctx.save();
        ctx.globalAlpha = 0.85;
        const iconX = fr.x + fr.w / 2 - 14;
        const iconY = fr.y - 8;
        drawGem(ctx, { x: iconX, y: iconY - 6, w: 12, h: 12 }, run.kind);
        text(ctx, label, iconX + 15, iconY, 10, labelColor, 'left', MONO);
        if (tierBadge) {
          text(ctx, tierBadge, iconX + 15 + ctx.measureText(label).width + 3, iconY, 8, PALETTE.gold, 'left', MONO);
        }
        ctx.restore();
      }
    }

    // Advance the simulation with a resolve so later enemies plan against
    // the state they will actually inherit.
    resolveBoardEnemy(simulation, enemy);
  }
}

/**
 * Cells that would match if this piece were dropped here. Simulated on a
 * throwaway copy — the live board is untouched, and the answer is derived
 * from the same matcher the game runs, so the preview cannot lie.
 */
function matchedCellsAfter(
  grid: readonly (GemKind | null)[],
  piece: Piece,
  rotation: number,
  column: number,
): number[] {
  const sim = grid.slice();
  const placed = rotatePiece(piece, rotation);
  const row = restRow(sim, placed.cells, column);
  if (row === null) return [];
  for (let i = 0; i < placed.cells.length; i++) {
    const off = placed.cells[i];
    const kind = placed.kinds[i];
    if (!off || !kind) continue;
    const r = row + off[0];
    const c = column + off[1];
    if (r < 0 || r >= WELL_ROWS || c < 0 || c >= WELL_COLS) continue;
    sim[r * WELL_COLS + c] = kind;
  }
  return [...findMatches(sim)];
}

function drawGhosts(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  combat: Combat,
  ctrl: Controller,
): void {
  const { aim, hover } = ctrl;
  if (aim.mode === 'idle' || !aim.card) return;
  const piece = aim.card.def.piece;

  if (aim.mode === 'piece' && piece) {
    const rawCol = hover.column ?? aim.column;
    const placed = rotatePiece(piece, aim.rotation);

    // Clamp the column so the piece's rightmost cell stays on the board.
    let maxDc = 0;
    for (const [, dc] of placed.cells) if (dc > maxDc) maxDc = dc;
    const col = Math.max(0, Math.min(rawCol, WELL_COLS - 1 - maxDc));

    const row = restRow(combat.grid, placed.cells, col);
    const ok = row !== null;
    for (let i = 0; i < placed.cells.length; i++) {
      const offset = placed.cells[i];
      const kind = placed.kinds[i];
      if (!offset || !kind) continue;
      const [dr, dc] = offset;
      const r = (row ?? 0) + dr;
      const c = col + dc;
      if (r < 0 || r >= WELL_ROWS || c < 0 || c >= WELL_COLS) continue;
      drawGem(
        ctx,
        cellRect(l, r * WELL_COLS + c),
        kind,
        ok ? 0.55 : 0.25,
        ok ? undefined : PALETTE.danger,
      );
    }
    // Column highlight bar.
    ctx.fillStyle = ok ? 'rgba(255,255,255,0.06)' : 'rgba(255,59,59,0.08)';
    ctx.fillRect(l.wellX + col * l.cell, l.well.y, l.cell, l.well.h);

    // The payoff, before you commit: outline every cell that would fire.
    // The info band below the well shows the arithmetic; this shows what.
    if (ok) {
      const fires = matchedCellsAfter(combat.grid, piece, aim.rotation, col);
      if (fires.length > 0) {
        const pulse = 0.45 + 0.3 * Math.sin(performance.now() / 130);
        for (const cell of fires) {
          const r = cellRect(l, cell);
          ctx.save();
          ctx.strokeStyle = `rgba(255,216,77,${pulse})`;
          ctx.lineWidth = 2.5;
          ctx.strokeRect(r.x + 2, r.y + 2, r.w - 4, r.h - 4);
          ctx.restore();
        }
      }
    }
    return;
  }

  if ((aim.mode === 'cell' || aim.mode === 'area') && hover.cell !== null) {
    const cr = idxRow(hover.cell);
    const cc = idxCol(hover.cell);
    const span = aim.mode === 'area' ? 1 : 0;
    for (let dr = -span; dr <= span; dr++) {
      for (let dc = -span; dc <= span; dc++) {
        const r = cr + dr;
        const c = cc + dc;
        if (r < 0 || r >= WELL_ROWS || c < 0 || c >= WELL_COLS) continue;
        highlightCell(ctx, cellRect(l, r * WELL_COLS + c), 'rgba(255,255,255,0.18)');
      }
    }
    return;
  }

  if (aim.mode === 'column' && hover.column !== null) {
    ctx.fillStyle = 'rgba(255,216,77,0.14)';
    ctx.fillRect(l.wellX + hover.column * l.cell, l.well.y, l.cell, l.well.h);
    return;
  }

  if (aim.mode === 'color' && hover.color !== null) {
    for (let i = 0; i < combat.grid.length; i++) {
      if (combat.grid[i] === hover.color) {
        highlightCell(ctx, cellRect(l, i), 'rgba(255,90,60,0.28)');
      }
    }
    return;
  }

  if (aim.mode === 'swap') {
    if (aim.first !== null) {
      highlightCell(ctx, cellRect(l, aim.first), 'rgba(94,200,232,0.35)');
    } else if (hover.cell !== null) {
      highlightCell(ctx, cellRect(l, hover.cell), 'rgba(94,200,232,0.18)');
    }
  }
}

function highlightCell(ctx: CanvasRenderingContext2D, r: Rect, fill: string): void {
  ctx.fillStyle = fill;
  ctx.fillRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
}

/**
 * The aim preview: the full simulated outcome of this play, showing the
 * calculation. The player learns the scoring system by making moves — not by
 * reading a rules page. Format:
 *
 *   🔥 4 Ember → 8 DMG
 *   🔥 CHARGED ×2  5 → 20 DMG
 *   CASCADE ×2  ❄ 4 → 16 BLOCK
 *   ROW CLEAR +6
 */
function drawOutcomePreview(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  combat: Combat,
  card: CardInstance,
  target: PlayTarget | null,
): void {
  if (!target) return;
  const out = previewCard(combat, card, target);
  if (!out) return;
  if (out.waves.length === 0 && out.damage === 0 && out.block === 0 && out.energy === 0 && out.poison === 0) return;

  // Build the calculation lines.
  const lines: { kind: GemKind | null; text: string; color: string }[] = [];

  for (let w = 0; w < out.waves.length; w++) {
    const wave = out.waves[w];
    if (!wave) continue;

    // Cascade header for wave 2+.
    if (w > 0 && wave.cascadeMult > 1) {
      lines.push({ kind: null, text: `CASCADE ×${wave.cascadeMult}`, color: PALETTE.gold });
    }

    // Run details: "4 Ember → 8 DMG"
    for (const run of wave.runs) {
      const tierLabel = run.tier !== 'base' ? run.tier.toUpperCase() : null;
      const valueLabel = run.kind === 'ember' ? 'DMG'
        : run.kind === 'frost' ? 'BLOCK'
        : run.kind === 'venom' ? 'POISON'
        : 'VOLT';
      const prefix = tierLabel ? `${tierLabel} ×2  ` : '';
      lines.push({
        kind: run.kind,
        text: `${prefix}${run.size} → ${run.value} ${valueLabel}`,
        color: GEM_COLOR[run.kind],
      });
    }

    // Row clear burst.
    if (wave.rowsCleared > 0 && wave.rowBurst > 0) {
      lines.push({ kind: null, text: `ROW CLEAR +${wave.rowBurst}`, color: PALETTE.gold });
    }

    // Energy from Volt.
    if (wave.energy > 0 && w === 0) {
      lines.push({ kind: 'volt', text: `+${wave.energy} ENERGY`, color: GEM_COLOR.volt });
    }
  }

  // Fallback: if no wave detail but there are totals, show simple chips.
  if (lines.length === 0) {
    if (out.damage > 0) lines.push({ kind: 'ember', text: `${out.damage} DMG`, color: GEM_COLOR.ember });
    if (out.block > 0) lines.push({ kind: 'frost', text: `+${out.block} BLOCK`, color: GEM_COLOR.frost });
    if (out.energy > 0) lines.push({ kind: 'volt', text: `+${out.energy} ENERGY`, color: GEM_COLOR.volt });
    if (out.poison > 0) lines.push({ kind: 'venom', text: `+${out.poison} POISON`, color: GEM_COLOR.venom });
  }

  if (lines.length === 0) return;

  // Layout: compact panel below the well, or above if no room.
  const lineH = 18;
  const padX = 10;
  const padY = 6;
  const iconSz = 14;
  const maxTextW = Math.max(...lines.map(l2 => {
    ctx.font = `11px ${MONO}`;
    return ctx.measureText(l2.text).width;
  }), 60);
  const panelW = Math.min(l.well.w - 8, maxTextW + iconSz + padX * 2 + 8);
  const panelH = lines.length * lineH + padY * 2;

  let y = Math.round(l.well.y + l.well.h + 8);
  const maxY = l.hand.y - panelH - 6;
  if (y > maxY) y = Math.round(l.well.y - panelH - 8);

  const x = Math.round(l.well.x + (l.well.w - panelW) / 2);

  fillRound(ctx, { x, y, w: panelW, h: panelH }, 6, 'rgba(13,11,20,0.88)');
  strokeRound(ctx, { x, y, w: panelW, h: panelH }, 6, 'rgba(255,255,255,0.08)', 1);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const ly = y + padY + i * lineH + lineH / 2;
    let tx = x + padX;
    if (line.kind !== null) {
      drawGem(ctx, { x: tx, y: ly - iconSz / 2, w: iconSz, h: iconSz }, line.kind);
      tx += iconSz + 4;
    }
    text(ctx, line.text, tx, ly, 11, line.color, 'left', MONO);
  }
}

// --- entities -------------------------------------------------------------

function drawBar(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  ratio: number,
  fill: string,
  back: string,
): void {
  fillRound(ctx, r, 3, back);
  const w = Math.max(0, Math.min(1, ratio)) * r.w;
  if (w > 0) fillRound(ctx, { x: r.x, y: r.y, w, h: r.h }, 3, fill);
}

function intentText(intent: Intent): string {
  switch (intent.kind) {
    case 'attack':
      return `ATK ${intent.amount}`;
    case 'attackTimes':
      return `ATK ${intent.amount} x${intent.hits}`;
    case 'block':
      return `BLOCK ${intent.amount}`;
    case 'addStone':
      return `STONE x${intent.count}`;
    case 'pushUpStone':
      return `RAISE +${intent.rows}`;
    case 'hexColumn':
      return 'HEX COLUMN';
    case 'scramble':
      return `SCRAMBLE ${intent.count}`;
    case 'dropPiece':
      return `DROP ${intent.piece.cells.length}`;
    case 'multi':
      return intent.intents.map(intentText).join('  +  ');
  }
}

function intentColor(intent: Intent): string {
  switch (intent.kind) {
    case 'attack':
    case 'attackTimes':
      return PALETTE.danger;
    case 'block':
      return PALETTE.block;
    case 'addStone':
    case 'pushUpStone':
    case 'hexColumn':
      return PALETTE.inert;
    case 'scramble':
      return PALETTE.volt;
    case 'dropPiece':
      return PALETTE.ember;
    case 'multi':
      return PALETTE.gold;
  }
}

/** Draw a piece's cells in their real colours, scaled into a box. */
function drawPieceChip(ctx: CanvasRenderingContext2D, r: Rect, piece: Piece): void {
  let maxR = 0;
  let maxC = 0;
  for (const [dr, dc] of piece.cells) {
    if (dr > maxR) maxR = dr;
    if (dc > maxC) maxC = dc;
  }
  const s = Math.min(r.w / (maxC + 1), r.h / (maxR + 1));
  for (let i = 0; i < piece.cells.length; i++) {
    const offset = piece.cells[i];
    const kind = piece.kinds[i];
    if (!offset || !kind) continue;
    const [dr, dc] = offset;
    drawGem(ctx, { x: r.x + dc * s, y: r.y + dr * s, w: s, h: s }, kind, 0.95);
  }
}

// --- stone ----------------------------------------------------------------

/**
 * Stone is masonry, not a fifth gem. It has to read as inert obstruction at a
 * glance and never be mistaken for Frost, so it gets mortar seams, a chip, and
 * zero saturation — the opposite of a gem's facet and glow.
 */
function drawStoneBrick(ctx: CanvasRenderingContext2D, r: Rect): void {
  const pad = Math.max(1, r.h * 0.07);
  const box = { x: r.x + pad, y: r.y + pad, w: r.w - pad * 2, h: r.h - pad * 2 };
  const k = Math.min(radiusOf(r), box.w / 2, box.h / 2);

  ctx.globalAlpha = 1;
  fillRound(ctx, box, k, '#4a4453');

  // Mortar seam across the middle — the tell that this is brickwork.
  ctx.strokeStyle = 'rgba(13,11,20,0.55)';
  ctx.lineWidth = Math.max(1, r.h * 0.07);
  ctx.beginPath();
  ctx.moveTo(box.x + box.w * 0.1, box.y + box.h * 0.52);
  ctx.lineTo(box.x + box.w * 0.9, box.y + box.h * 0.52);
  ctx.stroke();

  // Vertical joint, offset so it does not read as a crosshair.
  ctx.beginPath();
  ctx.moveTo(box.x + box.w * 0.62, box.y + box.h * 0.12);
  ctx.lineTo(box.x + box.w * 0.62, box.y + box.h * 0.52);
  ctx.stroke();

  // Top-left highlight, bottom-right shade. No facet, no glow.
  const g = ctx.createLinearGradient(box.x, box.y, box.x + box.w, box.y + box.h);
  g.addColorStop(0, 'rgba(255,255,255,0.16)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.02)');
  g.addColorStop(1, 'rgba(0,0,0,0.42)');
  roundedRect(ctx, box, k);
  ctx.fillStyle = g;
  ctx.fill();

  // A chip knocked out of a corner.
  ctx.fillStyle = 'rgba(13,11,20,0.4)';
  ctx.beginPath();
  ctx.moveTo(box.x + box.w * 0.78, box.y);
  ctx.lineTo(box.x + box.w, box.y);
  ctx.lineTo(box.x + box.w, box.y + box.h * 0.22);
  ctx.closePath();
  ctx.fill();
}

function radiusOf(r: Rect): number {
  return r.h * 0.18;
}

/**
 * The upper-right of an enemy panel is the most valuable pixel real estate in
 * the game — it is where the player looks to learn what is about to happen.
 * Every graphic here is mechanical. Nothing decorative survives.
 */
function drawIntentPreview(ctx: CanvasRenderingContext2D, r: Rect, intent: Intent): void {
  switch (intent.kind) {
    case 'dropPiece':
      drawPieceChip(ctx, r, intent.piece);
      return;

    case 'addStone': {
      // Individual bricks, so "3 Stone" is countable without reading.
      const n = Math.min(intent.count, 6);
      const cols = Math.min(n, 4);
      const rows = Math.ceil(n / 4);
      const s = Math.min(r.w / cols, r.h / Math.max(rows, 1));
      for (let i = 0; i < n; i++) {
        const c = i % 4;
        const rr = Math.floor(i / 4);
        drawStoneBrick(ctx, {
          x: r.x + c * s + (r.w - cols * s) / 2,
          y: r.y + rr * s,
          w: s,
          h: s,
        });
      }
      return;
    }

    case 'pushUpStone': {
      // A stone row with an arrow over it: the floor is coming up.
      const rowH = r.h * 0.34;
      for (let c = 0; c < 3; c++) {
        drawStoneBrick(ctx, {
          x: r.x + (r.w / 3) * c,
          y: r.y + r.h - rowH,
          w: r.w / 3,
          h: rowH,
        });
      }
      ctx.strokeStyle = PALETTE.danger;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      const cx = r.x + r.w / 2;
      ctx.moveTo(cx, r.y + r.h - rowH - 4);
      ctx.lineTo(cx, r.y + 4);
      ctx.moveTo(cx - 6, r.y + 11);
      ctx.lineTo(cx, r.y + 3);
      ctx.lineTo(cx + 6, r.y + 11);
      ctx.stroke();
      return;
    }

    case 'hexColumn': {
      const s = r.h / 4;
      for (let i = 0; i < 4; i++) {
        drawStoneBrick(ctx, { x: r.x + (r.w - s) / 2, y: r.y + i * s, w: s, h: s });
      }
      return;
    }

    case 'attack': {
      drawSlash(ctx, r);
      text(ctx, String(intent.amount), r.x + r.w / 2, r.y + r.h / 2 + 1, 17, PALETTE.danger, 'center', MONO);
      return;
    }

    case 'attackTimes': {
      // Unambiguous: the per-hit number, the hit count, and the total.
      drawSlash(ctx, r);
      text(ctx, `${intent.amount}`, r.x + r.w / 2, r.y + r.h * 0.36, 16, PALETTE.danger, 'center', MONO);
      text(ctx, `x${intent.hits}`, r.x + r.w / 2, r.y + r.h * 0.62, 12, PALETTE.text, 'center', MONO);
      text(ctx, `=${intent.amount * intent.hits}`, r.x + r.w / 2, r.y + r.h * 0.86, 10, PALETTE.dim, 'center', MONO);
      return;
    }

    case 'block': {
      drawShield(ctx, r);
      text(ctx, String(intent.amount), r.x + r.w / 2, r.y + r.h / 2 + 1, 16, PALETTE.block, 'center', MONO);
      return;
    }

    case 'scramble': {
      text(ctx, '⇄', r.x + r.w / 2, r.y + r.h * 0.36, 22, PALETTE.volt, 'center', SANS);
      text(ctx, String(intent.count), r.x + r.w / 2, r.y + r.h * 0.78, 13, PALETTE.volt, 'center', MONO);
      return;
    }

    case 'multi': {
      // Both promised actions, side by side, never one hiding the other.
      const n = intent.intents.length;
      const w = r.w / n;
      for (let i = 0; i < n; i++) {
        const sub = intent.intents[i];
        if (!sub) continue;
        drawIntentPreview(ctx, { x: r.x + i * w + 2, y: r.y, w: w - 4, h: r.h }, sub);
      }
      return;
    }
  }
}

/** A struck-through slash mark behind an attack number. */
function drawSlash(ctx: CanvasRenderingContext2D, r: Rect): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(255,59,59,0.28)';
  ctx.lineWidth = Math.max(2, r.w * 0.09);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(r.x + r.w * 0.18, r.y + r.h * 0.82);
  ctx.lineTo(r.x + r.w * 0.82, r.y + r.h * 0.18);
  ctx.stroke();
  ctx.restore();
}

function drawShield(ctx: CanvasRenderingContext2D, r: Rect): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(94,200,232,0.35)';
  ctx.lineWidth = Math.max(2, r.w * 0.08);
  ctx.beginPath();
  const x = r.x + r.w / 2;
  const y = r.y + r.h * 0.2;
  const w = r.w * 0.34;
  const h = r.h * 0.6;
  ctx.moveTo(x - w, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + h * 0.55);
  ctx.quadraticCurveTo(x + w, y + h, x, y + h);
  ctx.quadraticCurveTo(x - w, y + h, x - w, y + h * 0.55);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

function drawEnemy(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  enemy: PresenterEnemy,
  targeted: boolean,
  hovered: boolean,
): void {
  const narrow = r.w < 100;
  const acting = enemy.acting;

  // Panel background — same language in both orientations.
  fillRound(
    ctx,
    r,
    8,
    acting ? '#1e2430' : targeted ? '#241c14' : hovered ? '#1c1826' : PALETTE.panel,
  );
  strokeRound(
    ctx,
    r,
    8,
    acting ? PALETTE.danger : targeted ? PALETTE.gold : PALETTE.panelEdge,
    acting || targeted ? 2.5 : 1,
  );
  if (targeted || acting) {
    // Accent bar on the side facing the well (left edge for right-side rails).
    const bar = narrow
      ? { x: r.x, y: r.y + 8, w: 3, h: r.h - 16 }
      : { x: r.x, y: r.y + 8, w: 4, h: r.h - 16 };
    fillRound(ctx, bar, 2, acting ? PALETTE.danger : PALETTE.gold);
  }

  // Block shell — same treatment both orientations.
  if (enemy.block > 0) {
    const shell = Math.min(1, enemy.block / 20);
    const inset = 3 - shell * 2;
    ctx.save();
    ctx.globalAlpha = 0.22 + shell * 0.3;
    ctx.strokeStyle = PALETTE.block;
    ctx.lineWidth = 1.5 + shell * 3;
    roundedRect(ctx, { x: r.x + inset, y: r.y + inset, w: r.w - inset * 2, h: r.h - inset * 2 }, 10);
    ctx.stroke();
    ctx.restore();
  }

  if (narrow) {
    // --- vertical rail layout ---
    // Intent owns the top — it is the most important question on the panel.
    const pad = 5;
    const previewSize = Math.min(r.w - pad * 2, Math.max(28, Math.round(r.h * 0.28)));
    const preview: Rect = {
      x: r.x + (r.w - previewSize) / 2,
      y: r.y + pad,
      w: previewSize,
      h: previewSize,
    };
    drawIntentPreview(ctx, preview, enemy.intent);

    // Intent label directly under the preview.
    const labelY = preview.y + preview.h + 8;
    const label = acting ? 'ACTING' : intentText(enemy.intent);
    const labelColor = acting ? PALETTE.danger : intentColor(enemy.intent);
    const labelSize = label.length > 10 ? 8 : 10;
    text(ctx, label, r.x + r.w / 2, labelY, labelSize, labelColor, 'center', MONO);

    // HP bar below the intent.
    const barY = labelY + 14;
    const barW = r.w - pad * 2;
    drawBar(ctx, { x: r.x + pad, y: barY, w: barW, h: 7 }, enemy.hp / enemy.maxHp, PALETTE.hp, PALETTE.hpBack);
    text(ctx, `${enemy.hp}`, r.x + r.w / 2, barY + 15, 9, PALETTE.text, 'center', MONO);

    // Name, small, below HP.
    const nameY = barY + 26;
    const nameSize = enemy.name.length > 8 ? 8 : 9;
    text(ctx, enemy.name, r.x + r.w / 2, nameY, nameSize, targeted ? PALETTE.gold : PALETTE.dim, 'center');

    // Status badges stacked below name.
    let badgeY = nameY + 14;
    if (enemy.block > 0) {
      text(ctx, `⛨${enemy.block}`, r.x + r.w / 2, badgeY, 9, PALETTE.block, 'center', MONO);
      badgeY += 13;
    }
    if (enemy.poison > 0) {
      const pulse = 0.55 + 0.25 * Math.sin(performance.now() / 260);
      ctx.save();
      ctx.globalAlpha = pulse;
      text(ctx, `☠${enemy.poison}`, r.x + r.w / 2, badgeY, 9, PALETTE.poison, 'center', MONO);
      ctx.restore();
    }

    // Targeting pulse.
    if (targeted && !acting) {
      const pulse = 0.5 + 0.3 * Math.sin(performance.now() / 300);
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = PALETTE.gold;
      ctx.beginPath();
      ctx.arc(r.x + r.w / 2, r.y + r.h - 8, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.restore();
    }
    return;
  }

  // --- horizontal panel layout (wide/desktop) ---
  const pad = targeted || acting ? 14 : 8;

  const previewSize = Math.min(48, Math.max(30, r.h - 12));
  const preview: Rect = { x: r.x + r.w - previewSize - 6, y: r.y + 5, w: previewSize, h: previewSize };
  drawIntentPreview(ctx, preview, enemy.intent);

  const textW = r.w - previewSize - pad - 14;

  text(ctx, enemy.name, r.x + pad, r.y + 12, r.h < 66 ? 12 : 14, targeted ? PALETTE.gold : PALETTE.text);

  drawBar(
    ctx,
    { x: r.x + pad, y: r.y + 24, w: Math.max(40, textW), h: 8 },
    enemy.hp / enemy.maxHp,
    PALETTE.hp,
    PALETTE.hpBack,
  );
  text(
    ctx,
    `${enemy.hp}/${enemy.maxHp}`,
    r.x + pad + Math.max(40, textW) + 6,
    r.y + 28,
    10,
    PALETTE.dim,
    'left',
    MONO,
  );

  let badgeX = r.x + pad;
  const badgeY = r.y + 39;
  if (enemy.block > 0) {
    text(ctx, `BLOCK ${enemy.block}`, badgeX, badgeY, 10, PALETTE.block, 'left', MONO);
    const pips = Math.min(6, Math.ceil(enemy.block / 4));
    const pw = 4;
    for (let i = 0; i < pips; i++) {
      fillRound(ctx, { x: badgeX + 58 + i * (pw + 2), y: badgeY - 3, w: pw, h: 4 }, 1, PALETTE.block);
    }
    badgeX += 58 + pips * 6 + 6;
  }
  if (enemy.poison > 0) {
    const pulse = 0.55 + 0.25 * Math.sin(performance.now() / 260);
    text(ctx, `POISON ${enemy.poison}`, badgeX, badgeY, 10, PALETTE.poison, 'left', MONO);
  }

  const label = acting ? 'ACTING' : intentText(enemy.intent);
  const labelColor = acting ? PALETTE.danger : intentColor(enemy.intent);
  const labelSize = label.length > 14 ? 10 : 12;
  text(ctx, label, r.x + pad, r.y + r.h - 13, labelSize, labelColor, 'left', MONO);

  if (targeted && !acting) {
    const pulse = 0.5 + 0.3 * Math.sin(performance.now() / 300);
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = PALETTE.gold;
    ctx.beginPath();
    ctx.arc(r.x + r.w - previewSize - 12, r.y + r.h - 12, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();
  }
}

// --- cards ----------------------------------------------------------------

function rarityColor(c: CardInstance): string {
  switch (c.def.rarity) {
    case 'uncommon':
      return PALETTE.rarityUncommon;
    case 'rare':
      return PALETTE.rarityRare;
    default:
      return PALETTE.rarityCommon;
  }
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  card: CardInstance,
  affordable: boolean,
  selected: boolean,
  hovered: boolean,
): void {
  const lift = hovered && !selected ? 16 : 0;
  const box = { x: r.x, y: r.y - lift, w: r.w, h: r.h };

  ctx.save();
  if (!affordable) ctx.globalAlpha = 0.45;

  fillRound(ctx, box, 8, PALETTE.panel);
  strokeRound(ctx, box, 8, selected ? PALETTE.gold : rarityColor(card), selected ? 2 : 1);

  // Cost orb. Always the most legible thing on the card.
  const orb = { x: box.x + 6, y: box.y + 6, w: 24, h: 24 };
  fillRound(ctx, orb, 12, card.def.cost === 0 ? PALETTE.volt : PALETTE.stone);
  text(ctx, String(card.def.cost), orb.x + 12, orb.y + 13, 14, PALETTE.void, 'center', MONO);

  if (card.def.power === true) {
    text(ctx, 'POWER', box.x + box.w - 6, box.y + 18, 9, PALETTE.volt, 'right', MONO);
  }

  const hasPiece = card.def.piece !== undefined;

  if (hasPiece) {
    // Tetromino-first: the shape is the card. It owns the middle of the frame
    // in its real colours and true orientation, so the player can decide from
    // across the table without reading a word.
    text(ctx, card.def.name, box.x + 36, box.y + 18, 11, PALETTE.text);

    const area = {
      x: box.x + 10,
      y: box.y + 32,
      w: box.w - 20,
      h: box.h * 0.52,
    };
    drawPiecePreviewCentred(ctx, area, card);

    // One short effect line, never a paragraph.
    const lines = wrap(ctx, card.def.text, box.w - 12, 9);
    const bodyY = box.y + 32 + box.h * 0.52 + 4;
    const first = lines[0];
    if (first) text(ctx, first, box.x + box.w / 2, bodyY + 5, 9, PALETTE.dim, 'center', SANS);
    if (lines.length > 1) {
      text(ctx, lines[1] ?? '', box.x + box.w / 2, bodyY + 16, 9, PALETTE.dim, 'center', SANS);
    }
  } else {
    // Utility and Power cards stay icon-led so they can never be mistaken for
    // something you place.
    text(ctx, card.def.name, box.x + 36, box.y + 18, 11, PALETTE.text);
    drawCardGlyph(ctx, { x: box.x + (box.w - 40) / 2, y: box.y + 32, w: 40, h: 40 }, card.def.target);

    const bodyY = box.y + 78;
    const lines = wrap(ctx, card.def.text, box.w - 12, 9);
    for (let i = 0; i < Math.min(lines.length, 5); i++) {
      const line = lines[i];
      if (line) text(ctx, line, box.x + 6, bodyY + i * 11, 9, PALETTE.dim);
    }
  }

  // Socketed enchantments. A card carrying a stone must be identifiable at a
  // glance — that is the entire reason enchantments live on the instance.
  if (card.enchants.length > 0) {
    let bx = box.x + 6;
    const by = box.y + box.h - 10;
    ctx.font = `9px ${MONO}`;
    for (const e of card.enchants) {
      const def = enchantById(e);
      const label = `◆ ${def?.name ?? e}`;
      const w = ctx.measureText(label).width + 8;
      fillRound(ctx, { x: bx, y: by - 9, w, h: 13 }, 4, 'rgba(77,184,255,0.2)');
      text(ctx, label, bx + 4, by - 2, 9, PALETTE.rarityUncommon, 'left', MONO);
      bx += w + 4;
    }
  }

  ctx.restore();
}

/** Centre a piece in a box, scaled to fit, preserving its true orientation. */
function drawPiecePreviewCentred(
  ctx: CanvasRenderingContext2D,
  area: Rect,
  card: CardInstance,
): void {
  const piece = card.def.piece;
  if (!piece) return;
  let maxR = 0;
  let maxC = 0;
  for (const [dr, dc] of piece.cells) {
    if (dr > maxR) maxR = dr;
    if (dc > maxC) maxC = dc;
  }
  const cols = maxC + 1;
  const rows = maxR + 1;
  const s = Math.min(area.w / cols, area.h / rows);
  const ox = area.x + (area.w - s * cols) / 2;
  const oy = area.y + (area.h - s * rows) / 2;
  for (let i = 0; i < piece.cells.length; i++) {
    const offset = piece.cells[i];
    const kind = piece.kinds[i];
    if (!offset || !kind) continue;
    const [dr, dc] = offset;
    drawGem(ctx, { x: ox + dc * s, y: oy + dr * s, w: s, h: s }, kind);
  }
}

/** A mark that says what the card aims at, without pretending to be a piece. */
function drawCardGlyph(ctx: CanvasRenderingContext2D, r: Rect, mode: TargetMode): void {
  ctx.save();
  ctx.strokeStyle = PALETTE.dim;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;

  switch (mode) {
    case 'none':
      ctx.strokeStyle = PALETTE.gold;
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i + Math.PI / 4;
        ctx.moveTo(cx + Math.cos(a) * 6, cy + Math.sin(a) * 6);
        ctx.lineTo(cx + Math.cos(a) * 15, cy + Math.sin(a) * 15);
      }
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case 'cell':
      ctx.strokeRect(cx - 11, cy - 11, 22, 22);
      ctx.beginPath();
      ctx.moveTo(cx, cy - 16);
      ctx.lineTo(cx, cy - 11);
      ctx.moveTo(cx, cy + 11);
      ctx.lineTo(cx, cy + 16);
      ctx.moveTo(cx - 16, cy);
      ctx.lineTo(cx - 11, cy);
      ctx.moveTo(cx + 11, cy);
      ctx.lineTo(cx + 16, cy);
      ctx.stroke();
      break;
    case 'swap':
      ctx.beginPath();
      ctx.moveTo(cx - 12, cy - 6);
      ctx.lineTo(cx + 6, cy - 6);
      ctx.moveTo(cx + 1, cy - 11);
      ctx.lineTo(cx + 7, cy - 6);
      ctx.lineTo(cx + 1, cy - 1);
      ctx.moveTo(cx + 12, cy + 6);
      ctx.lineTo(cx - 6, cy + 6);
      ctx.moveTo(cx - 1, cy + 1);
      ctx.lineTo(cx - 7, cy + 6);
      ctx.lineTo(cx - 1, cy + 11);
      ctx.stroke();
      break;
    case 'column':
      ctx.strokeRect(cx - 7, cy - 17, 14, 34);
      ctx.beginPath();
      ctx.moveTo(cx, cy - 22);
      ctx.lineTo(cx, cy - 17);
      ctx.stroke();
      break;
    case 'color':
      ctx.strokeStyle = PALETTE.ember;
      ctx.beginPath();
      ctx.arc(cx, cy, 13, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = PALETTE.frost;
      ctx.beginPath();
      ctx.arc(cx, cy, 7, 0, Math.PI * 2);
      ctx.stroke();
      break;
  }
  ctx.restore();
}

// --- legend ---------------------------------------------------------------

const GEM_ROLE: Record<GemKind, string> = {
  ember: 'damage',
  frost: 'block',
  venom: 'poison',
  volt: 'energy',
  stone: 'inert',
};

// The always-on gem legend is gone. It was instruction-manual chrome competing
// for the same space as the board; colour identity now has to come from the
// gems themselves, which it already does.

// --- status bar -----------------------------------------------------------

/**
 * One horizontal row replaces what used to be an avatar box, a YOU panel, an
 * energy/discard text block, a relics list and an oversized End Turn button.
 *
 *   [portrait] ❤ 80/80  ⛨ 0  ⚡ 3/3  [5][0]  [ END ]
 *
 * Chrome compresses; the well does not.
 */
/**
 * Vertical HP bar. Fills bottom-to-top. Optional projected-loss segment
 * shows committed status damage (player) or aimed move damage (enemy)
 * as a translucent overlay at the top of the current HP.
 */
function drawVerticalHpBar(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  current: number,
  max: number,
  projectedLoss = 0,
  opts: { lethal?: boolean; color?: string; lossColor?: string } = {},
): void {
  const fill = opts.color ?? PALETTE.hp;
  const loss = opts.lossColor ?? 'rgba(255,80,80,0.45)';
  const back = PALETTE.hpBack;

  // Background track.
  fillRound(ctx, r, 3, back);

  const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
  const fillH = Math.round(r.h * ratio);
  const fillY = r.y + r.h - fillH;

  // Current HP fill — bottom to top.
  if (fillH > 0) {
    fillRound(ctx, { x: r.x, y: fillY, w: r.w, h: fillH }, 3, fill);
  }

  // Projected loss segment — translucent overlay at top of current fill.
  if (projectedLoss > 0 && fillH > 0) {
    const lossRatio = max > 0 ? Math.min(ratio, projectedLoss / max) : 0;
    const lossH = Math.round(r.h * lossRatio);
    if (lossH > 0) {
      ctx.save();
      ctx.globalAlpha = 0.7;
      fillRound(ctx, { x: r.x, y: fillY, w: r.w, h: lossH }, 3, loss);
      ctx.restore();
      // Dashed top edge on the loss segment.
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(r.x, fillY + lossH);
      ctx.lineTo(r.x + r.w, fillY + lossH);
      ctx.stroke();
      ctx.restore();
    }
  }

  // Lethal indicator — the bar is fully consumed.
  if (opts.lethal) {
    ctx.save();
    ctx.globalAlpha = 0.5 + 0.3 * Math.sin(performance.now() / 200);
    ctx.strokeStyle = '#ff2222';
    ctx.lineWidth = 2;
    roundedRect(ctx, r, 3);
    ctx.stroke();
    ctx.restore();
  }

  // Subtle border.
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.1)';
  ctx.lineWidth = 1;
  roundedRect(ctx, r, 3);
  ctx.stroke();
  ctx.restore();
}

/**
 * Hero character rail — left side. Portrait and status badges in the content
 * zone; the HP bar is drawn separately as a narrow strip hugging the well's
 * left edge. Content is clipped to the rail rect, which ends where the bar
 * begins, so nothing here can ever paint over the bar. Gem equations are NOT
 * drawn here — they're tutorial information.
 */
function drawHeroRail(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  p: Presenter,
  chars: CharViews | undefined,
): void {
  const r = l.heroRail;
  fillRound(ctx, r, 6, PALETTE.panel);
  strokeRound(ctx, r, 6, PALETTE.panelEdge, 1);

  ctx.save();
  roundedRect(ctx, r, 6);
  ctx.clip();

  const midX = r.x + r.w / 2;
  const textW = r.w - 4;

  // Portrait at top.
  if (chars) {
    ctx.save();
    roundedRect(ctx, l.avatar, 4);
    ctx.clip();
    drawPlayerChar(ctx, l.avatar, chars.player, p.playerHp / p.playerMaxHp);
    ctx.restore();
  }

  // Status icons near the HP bar — they modify health state.
  // Positioned at the bottom of the rail, closest to where the bar sits.
  let badgeY = r.y + r.h - 4;
  const badgeStep = 12;

  if (p.voltRemainder > 0) {
    const s = `${p.voltRemainder}/3`;
    text(ctx, s, midX, badgeY, fitSize(ctx, s, 7, textW, MONO), PALETTE.dim, 'center', MONO);
    badgeY -= badgeStep;
  }
  if (p.playerPoison > 0) {
    const s = `☠${p.playerPoison}`;
    text(ctx, s, midX, badgeY, fitSize(ctx, s, 9, textW, MONO), PALETTE.poison, 'center', MONO);
    badgeY -= badgeStep;
  }
  const energy = `⚡${p.playerEnergy}`;
  text(ctx, energy, midX, badgeY, fitSize(ctx, energy, 9, textW, MONO), PALETTE.volt, 'center', MONO);
  badgeY -= badgeStep;
  if (p.playerBlock > 0) {
    const s = `⛨${p.playerBlock}`;
    text(ctx, s, midX, badgeY, fitSize(ctx, s, 9, textW, MONO), PALETTE.block, 'center', MONO);
    badgeY -= badgeStep;
  }

  // HP number — current/max, between portrait and badges.
  const hpStr = `${p.playerHp}/${p.playerMaxHp}`;
  const hpY = (l.avatar.y + l.avatar.h + badgeY) / 2;
  text(ctx, hpStr, midX, hpY, fitSize(ctx, hpStr, 10, textW, MONO), PALETTE.text, 'center', MONO);

  ctx.restore();
}

/**
 * Draw the player's vertical HP bar — narrow strip hugging the well's left
 * edge. Shows current HP with projected status damage overlay.
 */
function drawHeroHpBar(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  p: Presenter,
): void {
  const r = l.heroHpBar;
  // Projected status damage — poison ticks are committed, not intent.
  const projectedLoss = p.playerPoison;
  drawVerticalHpBar(ctx, r, p.playerHp, p.playerMaxHp, projectedLoss, {
    lossColor: 'rgba(80,200,80,0.45)',
  });
}

/**
 * Contextual info band between well and hand. Shows scoring preview when
 * aiming. Empty when idle — no permanent chrome.
 */
function drawInfoBand(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  combat: Combat,
  ctrl: Controller,
): void {
  const r = l.infoBand;
  const aim = ctrl.aim;
  const isAiming = aim.mode !== 'idle' && aim.card !== undefined;

  if (!isAiming || !aim.card) return;

  const target = aimTarget(aim, ctrl.hover);
  const out = target ? previewCard(combat, aim.card, target) : null;

  fillRound(ctx, r, 6, 'rgba(13,11,20,0.7)');

  const card = aim.card;
  const midY = r.y + r.h / 2;
  let x = r.x + 8;

  // Card name + enchantments.
  text(ctx, card.def.name, x, midY - 8, 11, PALETTE.text, 'left', SANS);
  if (card.enchants.length > 0) {
    const enchNames = card.enchants.map(e => enchantById(e)?.name ?? e).join(', ');
    const nameW = ctx.measureText(card.def.name).width;
    text(ctx, `· ${enchNames}`, x + nameW + 4, midY - 8, 10, PALETTE.gold, 'left', SANS);
  }

  // Scoring preview lines.
  if (out && out.waves.length > 0) {
    let sx = r.x + 8;
    const sy = midY + 8;
    for (const wave of out.waves) {
      for (const run of wave.runs) {
        const tierLabel = run.tier !== 'base' ? ` ${run.tier.toUpperCase()}` : '';
        const valueLabel = run.kind === 'ember' ? 'DMG' : run.kind === 'frost' ? 'BLK' : run.kind === 'venom' ? 'PSN' : 'EN';
        drawGem(ctx, { x: sx, y: sy - 6, w: 12, h: 12 }, run.kind);
        const label = `${run.size}${tierLabel} → ${run.value} ${valueLabel}`;
        text(ctx, label, sx + 15, sy, 10, GEM_COLOR[run.kind], 'left', MONO);
        sx += 15 + ctx.measureText(label).width + 14;
      }
      if (wave.rowBurst > 0) {
        const lbl = `ROW +${wave.rowBurst}`;
        text(ctx, lbl, sx, sy, 10, PALETTE.gold, 'left', MONO);
        sx += ctx.measureText(lbl).width + 14;
      }
    }
  } else if (out) {
    const sy = midY + 8;
    let sx = r.x + 8;
    if (out.damage > 0) { text(ctx, `🔥${out.damage}`, sx, sy, 10, GEM_COLOR.ember, 'left', MONO); sx += 50; }
    if (out.block > 0) { text(ctx, `❄+${out.block}`, sx, sy, 10, GEM_COLOR.frost, 'left', MONO); sx += 50; }
    if (out.energy > 0) { text(ctx, `⚡+${out.energy}`, sx, sy, 10, GEM_COLOR.volt, 'left', MONO); sx += 50; }
    if (out.poison > 0) { text(ctx, `☠+${out.poison}`, sx, sy, 10, GEM_COLOR.venom, 'left', MONO); }
  }
}

/**
 * Enemy rail content — intent, HP number, statuses in the content zone right
 * of the bar. Content is clipped to that rect so it can never spill onto the
 * HP bar, which is drawn separately as a narrow strip hugging the well's
 * right edge.
 */
function drawEnemyRail(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  hpBar: Rect,
  enemy: PresenterEnemy,
  targeted: boolean,
  hovered: boolean,
  aimedDamage: number,
): void {
  const acting = enemy.acting;

  // Content panel — right of the bar, never under it.
  fillRound(ctx, r, 6, acting ? '#1e2430' : targeted ? '#241c14' : hovered ? '#1c1826' : PALETTE.panel);
  strokeRound(ctx, r, 6, acting ? PALETTE.danger : targeted ? PALETTE.gold : PALETTE.panelEdge, acting || targeted ? 2 : 1);

  ctx.save();
  roundedRect(ctx, r, 6);
  ctx.clip();

  const midX = r.x + r.w / 2;
  const textW = r.w - 4;
  let curY = r.y + 4;

  // Intent preview at top — most important question.
  const previewSize = Math.min(r.w - 8, Math.max(20, Math.round(r.h * 0.22)));
  const preview: Rect = { x: midX - previewSize / 2, y: curY, w: previewSize, h: previewSize };
  drawIntentPreview(ctx, preview, enemy.intent);
  curY += previewSize + 6;

  // Intent label.
  const label = acting ? 'ACTING' : intentText(enemy.intent);
  const labelColor = acting ? PALETTE.danger : intentColor(enemy.intent);
  const labelSize = fitSize(ctx, label, label.length > 10 ? 7 : 8, textW, MONO);
  text(ctx, label, midX, curY, labelSize, labelColor, 'center', MONO);
  curY += 12;

  // HP number.
  const hpStr = `${enemy.hp}`;
  text(ctx, hpStr, midX, curY, fitSize(ctx, hpStr, 10, textW, MONO), PALETTE.text, 'center', MONO);
  curY += 11;
  const maxStr = `/${enemy.maxHp}`;
  text(ctx, maxStr, midX, curY, fitSize(ctx, maxStr, 8, textW, MONO), PALETTE.dim, 'center', MONO);
  curY += 12;

  // Aimed damage label.
  if (aimedDamage > 0) {
    const lethal = aimedDamage >= enemy.hp;
    const dmgStr = `-${aimedDamage}`;
    text(ctx, dmgStr, midX, curY, fitSize(ctx, dmgStr, 9, textW, MONO), lethal ? '#ff4444' : '#ff8866', 'center', MONO);
    curY += 11;
    if (lethal) {
      text(ctx, 'LETHAL', midX, curY, 7, '#ff4444', 'center', MONO);
      curY += 10;
    }
  }

  // Status badges.
  if (enemy.block > 0) {
    const s = `⛨${enemy.block}`;
    text(ctx, s, midX, curY, fitSize(ctx, s, 8, textW, MONO), PALETTE.block, 'center', MONO);
    curY += 11;
  }
  if (enemy.poison > 0) {
    const s = `☠${enemy.poison}`;
    ctx.save();
    ctx.globalAlpha = 0.55 + 0.25 * Math.sin(performance.now() / 260);
    text(ctx, s, midX, curY, fitSize(ctx, s, 8, textW, MONO), PALETTE.poison, 'center', MONO);
    ctx.restore();
  }

  // Targeting indicator — subtle pulse.
  if (targeted && !acting) {
    ctx.save();
    ctx.globalAlpha = 0.4 + 0.3 * Math.sin(performance.now() / 300);
    ctx.fillStyle = PALETTE.gold;
    ctx.beginPath();
    ctx.arc(midX, r.y + r.h - 5, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.restore();

  // HP bar — narrow strip hugging the well edge, outside the content zone.
  const lethal = aimedDamage > 0 && aimedDamage >= enemy.hp;
  drawVerticalHpBar(ctx, hpBar, enemy.hp, enemy.maxHp, aimedDamage, {
    lethal,
    lossColor: 'rgba(255,120,60,0.5)',
  });
}

/**
 * Status strip — minimal. Draw/discard icons, End Turn. Nothing else
 * permanently. Contextual information lives in the info band above.
 */
function drawStatusStrip(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  p: Presenter,
  endHovered: boolean,
): void {
  // Draw/discard — tiny count badges.
  drawCountBadge(ctx, l.deckBadge, String(p.drawPile), PALETTE.dim);
  drawCountBadge(ctx, l.discardBadge, String(p.discardPile), PALETTE.dim);

  // Deck/discard labels — very small.
  text(ctx, 'DRAW', l.deckBadge.x + l.deckBadge.w / 2, l.deckBadge.y - 6, 7, PALETTE.dim, 'center', MONO);
  text(ctx, 'DISC', l.discardBadge.x + l.discardBadge.w / 2, l.discardBadge.y - 6, 7, PALETTE.dim, 'center', MONO);

  // End Turn — the one tactically urgent element.
  const enabled = p.phase === 'play';
  ctx.save();
  if (!enabled) ctx.globalAlpha = 0.4;
  fillRound(ctx, l.endTurn, 8, endHovered && enabled ? PALETTE.wellLip : PALETTE.stone);
  strokeRound(ctx, l.endTurn, 8, endHovered && enabled ? PALETTE.gold : PALETTE.panelEdge, 1);
  text(ctx, 'END', l.endTurn.x + l.endTurn.w / 2, l.endTurn.y + l.endTurn.h / 2, 13, PALETTE.text, 'center', MONO);
  ctx.restore();

  // Powers/relics — tiny line below strip.
  const extras: string[] = [];
  for (const pw of p.powers) extras.push(pw.stacks > 1 ? `${pw.name} x${pw.stacks}` : pw.name);
  for (const relic of p.relics) extras.push(relic.name);
  if (extras.length > 0) {
    text(ctx, extras.join('  ·  '), l.status.x + 2, l.status.y + l.status.h + 10, 8, PALETTE.gold, 'left', SANS);
  }
}

function drawCountBadge(ctx: CanvasRenderingContext2D, r: Rect, value: string, color: string): void {
  fillRound(ctx, r, 4, 'rgba(0,0,0,0.35)');
  text(ctx, value, r.x + r.w / 2, r.y + r.h / 2, 12, color, 'center', MONO);
}

// --- overlay --------------------------------------------------------------

function drawOverlay(ctx: CanvasRenderingContext2D, l: Layout, combat: Combat): void {
  if (combat.phase !== 'won' && combat.phase !== 'lost') return;
  ctx.fillStyle = 'rgba(13,11,20,0.82)';
  ctx.fillRect(0, 0, l.width, l.height);
  const won = combat.phase === 'won';
  text(
    ctx,
    won ? 'THE WELL IS QUIET' : 'YOU ARE BURIED',
    l.width / 2,
    l.height / 2 - 16,
    34,
    won ? PALETTE.good : PALETTE.danger,
    'center',
    MONO,
  );
  text(ctx, 'Press R to fight again', l.width / 2, l.height / 2 + 24, 14, PALETTE.dim, 'center', MONO);
}

/**
 * Gravity easing for a falling gem: 0→1 and convex, so the gem starts slow
 * and accelerates. Plain quadratic — the steeper curve and the vertical
 * stretch that went with it read as wrong on playtest. Exported so the
 * property can be pinned: this has drifted before.
 */
export function fallEasing(k: number): number {
  const t = Math.max(0, Math.min(1, k));
  return t * t;
}

/**
 * Kept exported for symmetry with fallEasing, but a fall is short enough at
 * phone scale that stretching the gem added motion noise rather than legible
 * speed. Always 1.
 */
export function fallStretch(_k: number): number {
  return 1;
}

// --- entry ----------------------------------------------------------------

export interface DisplayState {
  /** Grid to draw. Playback's display grid while a cascade is animating. */
  readonly grid: readonly (GemKind | null)[];
  /** Cells pulsing before they clear. */
  readonly flashing: readonly number[];
  /** 0→1 progress of the current pulse. */
  readonly flashK: number;
  /**
   * An enemy piece mid-drop. These cells are already in `grid`; the renderer
   * suppresses them there and draws them falling instead, so the player sees
   * the piece arrive rather than teleport.
   */
  readonly falling?: {
    readonly cells: readonly number[];
    readonly kinds: readonly GemKind[];
    readonly k: number;
    /**
     * Row each cell starts from. Present for post-match gravity, where every
     * survivor drops from its own height; absent for a piece entering from
     * above the lip, where they all share one.
     */
    readonly fromRows?: readonly number[] | undefined;
  } | undefined;
  /**
   * Frozen display state for an enemy-turn beat. Without it the renderer reads
   * live combat, which has already moved on — the panel would show next turn's
   * intent while this turn's attack is still landing.
   */
  readonly view?: TurnView | undefined;
  /** Enemy whose beat is on screen, for the acting highlight. */
  readonly actingEnemy?: number | undefined;
}

/**
 * Development overlay. Shows what the layout believes each region is, plus the
 * safe-area bands a notch or home indicator would claim. Invisible in normal
 * play; invaluable when a phone-sized viewport behaves unexpectedly.
 */
export function drawDebugBounds(ctx: CanvasRenderingContext2D, l: Layout): void {
  const boxes: [Rect, string, string][] = [
    [l.stage, 'STAGE', '#e8c45a'],
    [l.enemyZone, 'ENEMY', '#ff5a3c'],
    [l.well, 'WELL', '#4db8ff'],
    [l.heroRail, 'HERO', '#6ee86e'],
    [l.heroHpBar, 'HP', '#ff6b6b'],
    [l.infoBand, 'INFO', '#b07de0'],
    [l.hand, 'HAND', '#b07de0'],
    [l.status, 'STATUS', '#8a8298'],
    [l.endTurn, 'END', '#ffd84d'],
  ];

  ctx.save();
  ctx.lineWidth = 1;
  ctx.font = '9px ui-monospace, Menlo, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  for (const [r, label, color] of boxes) {
    if (r.w <= 0 || r.h <= 0) continue;
    ctx.strokeStyle = color;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    ctx.fillStyle = color;
    ctx.fillText(label, r.x + 2, r.y + 2);
  }

  // Enemy rail sections + HP bars.
  for (let i = 0; i < l.enemies.length; i++) {
    const r = l.enemies[i];
    if (!r) continue;
    ctx.strokeStyle = '#ff5a3c';
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    ctx.fillStyle = '#ff5a3c';
    ctx.fillText(`E${i}`, r.x + 2, r.y + 2);
    const hp = l.enemyHpBars[i];
    if (hp) {
      ctx.strokeStyle = '#ff6b6b';
      ctx.strokeRect(hp.x + 0.5, hp.y + 0.5, hp.w - 1, hp.h - 1);
    }
  }

  // Safe-area bands.
  ctx.fillStyle = 'rgba(255,59,59,0.18)';
  if (l.safe.top > 0) ctx.fillRect(0, 0, l.width, l.safe.top);
  if (l.safe.bottom > 0) ctx.fillRect(0, l.height - l.safe.bottom, l.width, l.safe.bottom);
  if (l.safe.left > 0) ctx.fillRect(0, 0, l.safe.left, l.height);
  if (l.safe.right > 0) ctx.fillRect(l.width - l.safe.right, 0, l.safe.right, l.height);

  ctx.fillStyle = PALETTE.text;
  ctx.fillText(`${l.width}x${l.height}  cell ${l.cell}`, 6, Math.max(0, l.height - 16));
  ctx.restore();
}

export interface CharViews {
  readonly enemy: ReadonlyMap<number, CharAnim<EnemyCharState>>;
  readonly player: CharAnim<PlayerCharState>;
}

/** A card currently held by the pointer, drawn at the pointer instead of in the fan. */
export interface DragView {
  readonly x: number;
  readonly y: number;
  readonly index: number;
}

export function drawGame(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  combat: Combat,
  ctrl: Controller,
  display?: DisplayState,
  chars?: CharViews,
  drag?: DragView | null,
  tiltOn = false,
): void {
  const grid = display?.grid ?? combat.grid;
  const falling = display?.falling;
  const hidden = new Set<number>(falling ? falling.cells : []);

  drawWell(ctx, l);

  // Tier escalation labels down the well's left edge. Subtle — these teach
  // the scoring ladder by being present, not by demanding attention.
  {
    const mm = getMatchMin();
    const tiers: [number, string][] = [
      [mm, 'BASE'],
      [mm + 1, 'CHRG'],
      [mm + 2, 'SRGE'],
      [mm + 3, 'AETH'],
    ];
    for (const [size, label] of tiers) {
      const row = WELL_ROWS - size;
      if (row < 0) continue;
      const y = l.wellY + row * l.cell + l.cell / 2;
      text(ctx, String(size), l.well.x - 4, y - 5, 8, 'rgba(255,255,255,0.12)', 'right', MONO);
      text(ctx, label, l.well.x - 4, y + 5, 7, 'rgba(255,255,255,0.08)', 'right', MONO);
    }
  }

  for (let i = 0; i < grid.length; i++) {
    if (hidden.has(i)) continue;
    const kind = grid[i] ?? null;
    if (kind !== null) drawGem(ctx, cellRect(l, i), kind);
  }

  // Falling gems. With fromRows each one drops from its own height (the gap a
  // clear just opened); without, they enter together from above the lip.
  if (falling && falling.cells.length > 0) {
    const shared = falling.fromRows === undefined;
    let minRow = WELL_ROWS;
    if (shared) {
      for (const c of falling.cells) minRow = Math.min(minRow, idxRow(c));
    }
    for (let n = 0; n < falling.cells.length; n++) {
      const cell = falling.cells[n];
      const kind = falling.kinds[n];
      if (cell === undefined || !kind) continue;
      const base = cellRect(l, cell);
      const toRow = idxRow(cell);
      const fromRow = shared ? minRow : (falling.fromRows?.[n] ?? toRow);
      // Ease so a gem accelerates into its slot rather than drifting.
      // Gravity. The gem starts slow and accelerates — no stretch, no drama,
      // just a short fall that behaves like one.
      const t = falling.k;
      const eased = fallEasing(t);
      const startOffset = shared ? -(minRow + 1.4) : (fromRow - toRow);
      const y = base.y + startOffset * (1 - eased) * l.cell;

      // A hair of squash on landing. Bottom edge stays anchored so the gem
      // compresses into its slot rather than shrinking toward its centre.
      const landed = t > 0.86;
      const sq = landed
        ? 1 - 0.12 * Math.sin(((t - 0.86) / 0.14) * Math.PI)
        : 1;
      const h = base.h * sq;
      drawGem(ctx, { x: base.x, y: y + (base.h - h), w: base.w, h }, kind);
    }
  }

  // The telegraph: cells about to clear pulse white before they go.
  if (display && display.flashing.length > 0) {
    const k = display.flashK;
    for (const cell of display.flashing) {
      const r = cellRect(l, cell);
      ctx.save();
      ctx.globalAlpha = 0.25 + k * 0.65;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
      ctx.restore();
    }
  }

  // The enemy's promised drop, under the player's own ghost.
  if (!display) drawEnemyGhosts(ctx, l, combat);

  drawGhosts(ctx, l, combat, ctrl);

  // A column with an occupied top row is about to bill 8 damage. Flag it.
  for (let c = 0; c < WELL_COLS; c++) {
    if (grid[c] === null || grid[c] === undefined) continue;
    ctx.fillStyle = 'rgba(255,59,59,0.22)';
    ctx.fillRect(l.wellX + c * l.cell, l.well.y, l.cell, 4);
  }

  // Everything below reads the presenter, never live combat directly.
  const p = present(combat, display?.view, display?.actingEnemy ?? null);

  // Compute aimed damage per enemy for the HP bar preview.
  // Uses the existing preview simulation — no scoring logic duplicated.
  const aimedDamage: number[] = [];
  {
    const aimCard = ctrl.aim.card;
    const isAiming = ctrl.aim.mode !== 'idle' && aimCard !== undefined;
    for (let i = 0; i < p.enemies.length; i++) {
      if (!isAiming || !aimCard) { aimedDamage.push(0); continue; }
      const target = aimTarget(ctrl.aim, ctrl.hover);
      const out = target ? previewCard(combat, aimCard, target) : null;
      // Distribute total damage: targeted enemy gets it all, or spread for
      // Surge/Aether. For now, all damage goes to current target.
      if (out && i === p.targetIndex) {
        aimedDamage.push(out.damage);
      } else {
        aimedDamage.push(0);
      }
    }
  }

  // Characters layered over rails.
  for (let i = 0; i < p.enemies.length; i++) {
    const enemy = p.enemies[i];
    const bust = l.enemyBusts[i];
    if (!enemy || !chars) continue;
    const anim = chars.enemy.get(enemy.uid);
    if (!anim || !bust) continue;
    const idle = anim.state === 'idle';
    ctx.save();
    ctx.globalAlpha = idle ? 0.3 : 0.7;
    drawEnemyChar(ctx, bust, anim, enemy.hp / enemy.maxHp);
    ctx.restore();
  }

  // Enemy rails on the right — vertical HP bars with aimed damage preview.
  for (let i = 0; i < p.enemies.length; i++) {
    const enemy = p.enemies[i];
    const r = l.enemies[i];
    const hpBar = l.enemyHpBars[i];
    if (!enemy || !r || !hpBar) continue;
    drawEnemyRail(ctx, r, hpBar, enemy, i === p.targetIndex, ctrl.hover.enemyIndex === i, aimedDamage[i] ?? 0);
  }

  // Hero rail content on the left.
  drawHeroRail(ctx, l, p, chars);
  // Hero HP bar — narrow strip hugging the well's left edge.
  drawHeroHpBar(ctx, l, p);

  // Contextual info band — scoring preview when aiming, empty when idle.
  drawInfoBand(ctx, l, combat, ctrl);

  // Status strip below.
  drawStatusStrip(ctx, l, p, ctrl.hover.overEndTurn);

  if (p.showHand) {
    // Exactly one card is "focused" and drawn last, on top and larger: the one
    // under the pointer, the one already raised, or the one being hovered.
    // Drawing in fan order meant a selected card could sit behind a neighbour,
    // which is the one card the player is trying to read.
    const aimUid = ctrl.aim.card?.uid;
    let focusIndex = drag ? drag.index : -1;
    if (focusIndex < 0 && aimUid !== undefined) {
      focusIndex = combat.hand.findIndex(c => c.uid === aimUid);
    }
    if (focusIndex < 0 && ctrl.hover.cardIndex !== null) {
      focusIndex = ctrl.hover.cardIndex;
    }

    for (let i = 0; i < combat.hand.length; i++) {
      if (i === focusIndex) continue;
      const card = combat.hand[i];
      const r = l.cards[i];
      if (!card || !r) continue;
      drawCard(
        ctx,
        r,
        card,
        p.playerEnergy >= card.def.cost,
        false,
        false,
      );
    }

    const focused = focusIndex >= 0 ? combat.hand[focusIndex] : undefined;
    const focusedRect = focusIndex >= 0 ? l.cards[focusIndex] : undefined;
    if (focused && focusedRect) {
      // Dragged cards ride the pointer. Raised or hovered cards grow in place,
      // scaled up and lifted clear of the fan so the whole card is readable.
      if (drag) {
        const w = l.cardW * 1.15;
        const h = l.cardH * 1.15;
        drawCard(
          ctx,
          { x: drag.x - w / 2, y: drag.y - h * 0.75, w, h },
          focused,
          p.playerEnergy >= focused.def.cost,
          true,
          false,
        );
      } else {
        // "Pull forward" means exactly that. A merely hovered card comes
        // toward the player — it lifts, it does not scale and it does not
        // shift sideways. Only a card actually selected for play grows, and
        // even then it grows out of its own slot rather than travelling.
        const committed = aimUid !== undefined && aimUid === focused.uid;
        const scale = committed ? 1.16 : 1;
        const w = focusedRect.w * scale;
        const h = focusedRect.h * scale;
        const rawX = focusedRect.x - (w - focusedRect.w) / 2;
        const x = committed
          ? Math.max(l.stage.x + 4, Math.min(rawX, l.stage.x + l.stage.w - w - 4))
          : focusedRect.x;
        drawCard(
          ctx,
          { x, y: focusedRect.y - (h - focusedRect.h) - (committed ? 20 : 26), w, h },
          focused,
          p.playerEnergy >= focused.def.cost,
          committed,
          false,
        );
      }
    }
  } else {
    // The grip is in the pile. Saying so beats drawing nothing.
    text(
      ctx,
      'ENEMY TURN',
      l.hand.x + l.hand.w / 2,
      l.hand.y + l.hand.h / 2,
      16,
      PALETTE.dim,
      'center',
      MONO,
    );
  }

  // Aiming controls. Buttons, not gestures — touch must never need hover, a
  // right-click, or a remembered key. The gyroscope rotates the piece on a
  // phone, but the button stays because permission can be denied.
  if (ctrl.aim.mode !== 'idle') {
    if (ctrl.aim.mode === 'piece') {
      const label = tiltOn ? 'TILT / ROTATE' : 'ROTATE';
      drawButton(ctx, l.rotate, label, ctrl.hover.overRotate);
    }
    drawButton(ctx, l.cancel, 'CANCEL', ctrl.hover.overCancel);
  } else if (p.phase === 'play' && !p.freeSwapUsed) {
    // Free swap is an icon that lights up when available, not a sentence
    // permanently explaining itself on the board.
    drawSwapIcon(ctx, {
      x: l.well.x + l.well.w - 26,
      y: l.well.y - 26,
      w: 22,
      h: 22,
    });
  }

  drawOverlay(ctx, l, combat);

  if (debugBoundsOn()) drawDebugBounds(ctx, l);
}

/** Free-swap affordance: two arrows, glowing while the swap is still available. */
function drawSwapIcon(ctx: CanvasRenderingContext2D, r: Rect): void {
  ctx.save();
  const glow = 0.55 + 0.25 * Math.sin(performance.now() / 400);
  ctx.globalAlpha = glow;
  ctx.strokeStyle = PALETTE.block;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  ctx.beginPath();
  ctx.moveTo(r.x + 2, cy - 4);
  ctx.lineTo(r.x + r.w - 3, cy - 4);
  ctx.moveTo(r.x + r.w - 7, cy - 8);
  ctx.lineTo(r.x + r.w - 3, cy - 4);
  ctx.lineTo(r.x + r.w - 7, cy);
  ctx.moveTo(r.x + r.w - 3, cy + 4);
  ctx.lineTo(r.x + 2, cy + 4);
  ctx.moveTo(r.x + 6, cy);
  ctx.lineTo(r.x + 2, cy + 4);
  ctx.lineTo(r.x + 6, cy + 8);
  ctx.stroke();
  void cx;
  ctx.restore();
}

// --- run screens ----------------------------------------------------------

const NODE_COLOR: Record<NodeType, string> = {
  start: PALETTE.inert,
  fight: '#c04048',
  elite: '#e8913c',
  rest: PALETTE.good,
  store: PALETTE.rarityUncommon,
  boss: '#2a1a32',
};

const NODE_GLYPH: Record<NodeType, string> = {
  start: '·',
  fight: '',
  elite: '☠',
  rest: '+',
  store: '$',
  boss: '◆',
};

/** Region tint colors for map node backgrounds. */
const REGION_TINT: Record<string, string> = {
  stone: 'rgba(156,122,78,0.38)',
  prism: 'rgba(181,122,255,0.32)',
  crystal: 'rgba(77,184,255,0.32)',
  shard: 'rgba(138,130,152,0.28)',
  neutral: 'rgba(138,130,152,0.12)',
};

/** Solid region color for legend swatches. */
const REGION_SOLID: Record<string, string> = {
  stone: '#9c7a4e',
  prism: '#b57aff',
  crystal: '#4db8ff',
  shard: '#8a8298',
};

/** Human-readable region label. */
const REGION_LABEL: Record<string, string> = {
  stone: 'stone',
  prism: 'prism',
  crystal: 'crystal',
  shard: 'shard',
};

const TYPE_LABEL: Record<NodeType, string> = {
  start: 'Start',
  fight: 'Fight',
  elite: 'Elite',
  rest: 'Rest',
  store: 'Store',
  boss: 'Boss',
};

export interface MapViewOpts {
  readonly notice?: string | null | undefined;
  readonly hoverId?: string | null | undefined;
  /** Keyboard-focused node id — shows the same tooltip as hover. */
  readonly focusId?: string | null | undefined;
}

/**
 * The branching map. Node graph drawn bottom (start) to top (boss): the
 * current node pulses, available routes glow, visited nodes dim, and the
 * boss is always visible at the top.
 */
export function drawMap(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  opts?: MapViewOpts,
): void {
  ctx.fillStyle = PALETTE.void;
  ctx.fillRect(0, 0, l.width, l.height);

  const area = mapGraphArea(l);
  const map = run.map;
  const byId = new Map(map.nodes.map(n => [n.id, n]));
  const visited = new Set(run.visitedNodes);
  const available = new Set(nodesFrom(map, run.currentNodeId).map(n => n.id));
  // Fog of war: current node, everything within three forward hops, the
  // trail already walked, and the boss — which is always on the horizon.
  const visible = visibleNodes(map, run.currentNodeId, 3);
  for (const id of visited) visible.add(id);
  const boss = map.nodes.find(n => n.type === 'boss');
  if (boss) visible.add(boss.id);

  text(ctx, 'CHOOSE YOUR PATH', l.stage.x + l.stage.w / 2, l.stage.y + 28, 22, PALETTE.text, 'center', MONO);

  const drawn = (id: string): boolean => visible.has(id) || visited.has(id);

  // Connections first, so nodes sit on top of their edges.
  for (const node of map.nodes) {
    if (!drawn(node.id)) continue;
    const from = nodeCenter(node, area);
    for (const cid of node.connections) {
      const child = byId.get(cid);
      if (!child || !drawn(cid)) continue;
      const to = nodeCenter(child, area);
      const isChoice = node.id === run.currentNodeId && available.has(cid);
      const walked = visited.has(node.id) && visited.has(cid);
      ctx.save();
      ctx.strokeStyle = isChoice
        ? PALETTE.gold
        : walked
          ? 'rgba(232,226,240,0.4)'
          : 'rgba(255,255,255,0.12)';
      ctx.lineWidth = isChoice ? 2.5 : 1.5;
      if (isChoice) {
        ctx.shadowColor = PALETTE.gold;
        ctx.shadowBlur = 8;
      }
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      ctx.restore();
    }
  }

  const now = performance.now();
  for (const node of map.nodes) {
    const c = nodeCenter(node, area);
    const isCurrent = node.id === run.currentNodeId;
    const isAvail = available.has(node.id);
    const isVisited = visited.has(node.id) && !isCurrent;

    if (!drawn(node.id)) {
      // Not yet revealed: a faint placeholder, no label, no identity.
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.beginPath();
      ctx.arc(c.x, c.y, 6, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    const r = node.type === 'boss' ? 24 : node.type === 'start' ? 12 : 17;
    ctx.save();
    if (isVisited) ctx.globalAlpha = 0.35;

    // Region tint as subtle background halo behind the node circle.
    const tint = REGION_TINT[node.region ?? 'neutral'];
    if (tint && node.type !== 'start') {
      ctx.fillStyle = tint;
      ctx.beginPath();
      ctx.arc(c.x, c.y, r + 6, 0, Math.PI * 2);
      ctx.fill();
    }

    if (isAvail) {
      const hovered = opts?.hoverId === node.id || opts?.focusId === node.id;
      ctx.shadowColor = PALETTE.gold;
      ctx.shadowBlur = (hovered ? 20 : 14) + 6 * Math.sin(now / 280);
    }

    ctx.fillStyle = NODE_COLOR[node.type];
    ctx.beginPath();
    ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    const isHovered = opts?.hoverId === node.id || opts?.focusId === node.id;
    ctx.strokeStyle = isCurrent
      ? PALETTE.gold
      : isAvail
        ? isHovered
          ? '#fff2c0'
          : PALETTE.gold
        : node.type === 'boss'
          ? PALETTE.rarityRare
          : PALETTE.panelEdge;
    ctx.lineWidth = isCurrent ? 3 : isAvail ? 2.5 : 1.5;
    ctx.stroke();

    const glyph = NODE_GLYPH[node.type];
    if (glyph) {
      text(
        ctx,
        glyph,
        c.x,
        c.y + 1,
        node.type === 'boss' ? 20 : 14,
        node.type === 'boss' ? PALETTE.gold : '#0d0b14',
        'center',
        node.type === 'elite' ? SANS : MONO,
      );
    }

    if (isCurrent) {
      const pulse = 0.45 + 0.35 * Math.sin(now / 260);
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.strokeStyle = PALETTE.gold;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(c.x, c.y, r + 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    const label = node.type === 'elite' ? `${node.name} ☠` : node.name;
    text(
      ctx,
      label,
      c.x,
      c.y + r + 12,
      9,
      isAvail ? PALETTE.gold : isCurrent ? PALETTE.text : PALETTE.dim,
      'center',
      SANS,
    );

    // Boss alignment text below the boss node label.
    if (node.type === 'boss' && run.bossPreview) {
      text(
        ctx,
        run.bossPreview.alignment,
        c.x,
        c.y + r + 24,
        8,
        PALETTE.rarityRare,
        'center',
        SANS,
      );
    }

    if (isAvail) {
      const ordered = nodesFrom(map, run.currentNodeId);
      const n = ordered.findIndex(x => x.id === node.id);
      if (n >= 0) text(ctx, String(n + 1), c.x + r + 8, c.y - r - 2, 10, PALETTE.gold, 'center', MONO);
    }
    ctx.restore();
  }

  // Boss preview panel — top-right corner.
  if (run.bossPreview) {
    const bp = bossPreviewRect(l);
    fillRound(ctx, bp, 6, 'rgba(22,19,31,0.85)');
    strokeRound(ctx, bp, 6, PALETTE.panelEdge, 1);
    text(ctx, `BOSS: ${run.bossPreview.name}`, bp.x + 8, bp.y + 12, 11, PALETTE.gold, 'left', MONO);
    text(ctx, run.bossPreview.alignment, bp.x + 8, bp.y + 26, 9, PALETTE.rarityRare, 'left', SANS);
    text(ctx, run.bossPreview.hint, bp.x + 8, bp.y + 40, 8, PALETTE.dim, 'left', SANS);
  }

  // Region legend — only regions present on visible nodes.
  const legendRegions = new Set<string>();
  for (const node of map.nodes) {
    if (drawn(node.id) && node.region && node.region !== 'neutral') {
      legendRegions.add(node.region);
    }
  }
  if (legendRegions.size > 0) {
    const lr = regionLegendRect(l);
    text(ctx, 'Region:', lr.x, lr.y + lr.h / 2, 9, PALETTE.dim, 'left', SANS);
    let lx = lr.x + 48;
    for (const region of ['stone', 'prism', 'crystal', 'shard']) {
      if (!legendRegions.has(region)) continue;
      const color = REGION_SOLID[region] ?? PALETTE.dim;
      ctx.fillStyle = color;
      ctx.fillRect(lx, lr.y + lr.h / 2 - 4, 8, 8);
      const label = REGION_LABEL[region] ?? region;
      text(ctx, label, lx + 12, lr.y + lr.h / 2, 9, PALETTE.dim, 'left', SANS);
      lx += 12 + ctx.measureText(label).width + 16;
    }
  }

  // Tooltip for hovered or keyboard-focused visible node.
  const tipId = opts?.hoverId ?? opts?.focusId;
  if (tipId) {
    const node = byId.get(tipId);
    if (node && drawn(node.id)) {
      drawMapTooltip(ctx, node, area);
    }
  }

  // HP and progress below the graph.
  const cx = l.stage.x + l.stage.w / 2;
  const hudY = l.stage.y + l.stage.h - 58;
  const hpStr = `HP  ${run.hp}/${run.maxHp}`;
  const hpColor = run.hp <= run.maxHp * 0.35 ? PALETTE.danger : PALETTE.text;
  if (opts?.notice) {
    text(ctx, opts.notice, cx, hudY - 24, 14, PALETTE.good, 'center', MONO);
  }
  text(ctx, hpStr, cx, hudY, 14, hpColor, 'center', MONO);
  const progressLine = (() => {
    const totals = componentTotalsLine(run.components);
    return totals ? `${run.fightsWon} fights won  ·  ${totals}` : `${run.fightsWon} fights won`;
  })();
  text(ctx, progressLine, cx, hudY + 20, fitSize(ctx, progressLine, 11, l.stage.w - 24, MONO), PALETTE.dim, 'center', MONO);
  text(ctx, 'Click a glowing node to descend  ·  Prepare at rest nodes  ·  1-3 keys  ·  ←→ focus', cx, l.stage.y + l.stage.h - 16, 10, PALETTE.dim, 'center', SANS);
}

/** Tooltip near a map node showing name, type, hint, and enemy count. */
function drawMapTooltip(
  ctx: CanvasRenderingContext2D,
  node: MapNode,
  area: Rect,
): void {
  const c = nodeCenter(node, area);
  const lines: string[] = [node.name];
  lines.push(TYPE_LABEL[node.type] ?? node.type);
  if (node.hint) lines.push(node.hint);
  if (node.enemies && node.enemies.length > 0 && (node.type === 'fight' || node.type === 'elite')) {
    const n = node.enemies.length;
    lines.push(`${n} enem${n === 1 ? 'y' : 'ies'}`);
  }

  const pad = 8;
  const lineH = 13;
  ctx.font = `9px ${SANS}`;
  let tw = 0;
  for (const line of lines) {
    tw = Math.max(tw, ctx.measureText(line).width);
  }
  tw = Math.min(200, tw + pad * 2);
  const th = lines.length * lineH + pad * 2;

  // Position tooltip offset from node, preferring right side, flipping left near edge.
  let tx = c.x + 28;
  let ty = c.y - th / 2;
  if (tx + tw > area.x + area.w) tx = c.x - 28 - tw;
  if (ty < area.y) ty = area.y;
  if (ty + th > area.y + area.h) ty = area.y + area.h - th;

  fillRound(ctx, { x: tx, y: ty, w: tw, h: th }, 4, 'rgba(13,11,20,0.92)');
  strokeRound(ctx, { x: tx, y: ty, w: tw, h: th }, 4, PALETTE.panelEdge, 1);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const color = i === 0 ? PALETTE.text : i === 1 ? PALETTE.gold : PALETTE.dim;
    const font = i <= 1 ? MONO : SANS;
    text(ctx, line, tx + pad, ty + pad + i * lineH + lineH / 2, i === 0 ? 10 : 9, color, 'left', font);
  }
}


function drawButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  hovered: boolean,
): void {
  fillRound(ctx, r, 8, hovered ? PALETTE.wellLip : PALETTE.stone);
  strokeRound(ctx, r, 8, hovered ? PALETTE.gold : PALETTE.panelEdge, 1);
  text(ctx, label, r.x + r.w / 2, r.y + r.h / 2, 13, PALETTE.text, 'center', MONO);
}

export function drawTitle(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  seedCode: string,
  seedCopied: boolean,
): void {
  ctx.fillStyle = PALETTE.void;
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.width / 2;
  const cy = l.height / 2;

  // A quiet well, rendered as an outline, behind the title.
  const cell = Math.min(l.width, l.height) * 0.028;
  const gw = cell * WELL_COLS;
  const gh = cell * WELL_ROWS;
  const gx = cx - gw / 2;
  const gy = cy - gh / 2 - l.height * 0.06;
  ctx.strokeStyle = PALETTE.wellLip;
  ctx.lineWidth = 2;
  ctx.strokeRect(gx, gy, gw, gh);
  const gems: GemKind[] = ['ember', 'frost', 'venom', 'volt'];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < WELL_COLS; c++) {
      if ((r * 7 + c) % 3 === 0) continue;
      const k = gems[(r * 3 + c) % gems.length];
      if (!k) continue;
      drawGem(ctx, { x: gx + c * cell, y: gy + gh - (r + 1) * cell, w: cell, h: cell }, k, 0.85);
    }
  }

  text(ctx, 'GEMFALL', cx, cy + l.height * 0.16, 52, PALETTE.text, 'center', MONO);
  text(
    ctx,
    'Geometry is your weapon. Colour is your ammo.',
    cx,
    cy + l.height * 0.16 + 34,
    14,
    PALETTE.dim,
    'center',
    SANS,
  );
  text(ctx, 'Enter — descend', cx, cy + l.height * 0.16 + 70, 13, PALETTE.gold, 'center', MONO);
  drawSeedBadge(ctx, l, seedCode, seedCopied);
  text(ctx, 'click the seed to copy   ·   Q — match-3/4   ·   T — board size   ·   ?seed= re-enters a run', cx, cy + l.height * 0.16 + 96, 10, PALETTE.dim, 'center', SANS);
}

/** Coloured disc with the component's abbreviation — the drop's icon. */
function drawComponentIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  id: ComponentId,
): void {
  ctx.save();
  ctx.fillStyle = COMPONENT_COLOR[id];
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1;
  ctx.stroke();
  const abbr = COMPONENTS[id].abbr;
  text(ctx, abbr, x, y + 1, Math.max(8, Math.round(radius * 0.8)), '#0d0b14', 'center', MONO);
  ctx.restore();
}

function componentLabel(drop: ComponentDrop): string {
  const info = COMPONENTS[drop.id];
  return `${drop.count}× ${drop.count === 1 ? info.name : info.plural}`;
}

/** Compact accumulated totals, e.g. "5 EF  ·  2 MF  ·  1 IC". */
export function componentTotalsLine(components: Partial<Record<ComponentId, number>>): string {
  const parts: string[] = [];
  for (const id of Object.keys(COMPONENTS) as ComponentId[]) {
    const n = components[id];
    if (n !== undefined && n > 0) parts.push(`${n} ${COMPONENTS[id].abbr}`);
  }
  return parts.join('  ·  ');
}

/**
 * Component-only reward screen for normal fights. Shows what the fight
 * dropped, the run's accumulated totals, and a single Continue action —
 * no card choice, because routine fights no longer offer one.
 */
export function drawComponentReward(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  drops: readonly ComponentDrop[],
  components: Partial<Record<ComponentId, number>>,
  hoverContinue: boolean,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.94)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  const topY = Math.round(l.stage.y + l.stage.h * 0.26);

  text(ctx, 'THE WELL GIVES BACK', cx, topY, 24, PALETTE.text, 'center', MONO);
  text(ctx, drops.length > 0 ? 'You found:' : 'Nothing this time.', cx, topY + 30, 13, PALETTE.dim, 'center', SANS);

  let y = topY + 62;
  const iconR = 12;
  for (const drop of drops) {
    const label = componentLabel(drop);
    ctx.font = `14px ${MONO}`;
    const tw = ctx.measureText(label).width;
    const ix = cx - (iconR * 2 + 10 + tw) / 2 + iconR;
    drawComponentIcon(ctx, ix, y, iconR, drop.id);
    text(ctx, label, ix + iconR + 10, y, 14, PALETTE.text, 'left', MONO);
    y += 30;
  }

  const totals = componentTotalsLine(components);
  if (totals) {
    y += 8;
    const line = `TOTAL  ${totals}`;
    text(ctx, line, cx, y, fitSize(ctx, line, 11, l.stage.w - 24, MONO), PALETTE.dim, 'center', MONO);
  }

  drawButton(ctx, l.skipButton, 'CONTINUE  [Enter]', hoverContinue);
}

/**
 * One row, three kinds of slot. Cards, relics, and upgrades share the screen
 * so every floor asks the same question — what does this run become? — instead
 * of alternating between whole screens of a single kind.
 *
 * Elite and boss fights prepend the component drops above the slot grid: the
 * components are banked automatically, the slots are the choice.
 */
export function drawRewardScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  options: readonly RewardOption[],
  drops: readonly ComponentDrop[],
  components: Partial<Record<ComponentId, number>>,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.94)';
  ctx.fillRect(0, 0, l.width, l.height);

  const topY = l.rewardCards[0]?.y ?? l.height / 2 - 110;
  const hasDrops = drops.length > 0;
  text(ctx, 'THE WELL GIVES BACK', l.width / 2, hasDrops ? topY - 96 : topY - 48, 24, PALETTE.text, 'center', MONO);
  text(ctx, 'Take one. The rest is lost.', l.width / 2, hasDrops ? topY - 72 : topY - 20, 13, PALETTE.dim, 'center', SANS);

  if (hasDrops) {
    const rowY = topY - 44;
    ctx.font = `12px ${MONO}`;
    const labels = drops.map(d => ({ drop: d, label: componentLabel(d) }));
    const widths = labels.map(e => 22 + 6 + ctx.measureText(e.label).width);
    const totalW = widths.reduce((a, b) => a + b, 0) + Math.max(0, labels.length - 1) * 14;
    let x = l.width / 2 - totalW / 2;
    for (let i = 0; i < labels.length; i++) {
      const entry = labels[i];
      const w = widths[i];
      if (!entry || w === undefined) continue;
      drawComponentIcon(ctx, x + 11, rowY, 11, entry.drop.id);
      text(ctx, entry.label, x + 28, rowY, 12, PALETTE.text, 'left', MONO);
      x += w + 14;
    }
    const totals = componentTotalsLine(components);
    if (totals) {
      text(ctx, `TOTAL  ${totals}`, l.width / 2, topY - 18, 10, PALETTE.dim, 'center', MONO);
    }
  }

  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    const r = l.rewardCards[i];
    if (!opt || !r) continue;
    const hovered = hoverIndex === i;

    if (opt.kind === 'card' && opt.card) {
      drawCard(ctx, r, { def: opt.card, uid: -1 - i, enchants: [] }, true, false, hovered);
      drawSlotTag(ctx, r, 'NEW', PALETTE.rarityCommon);
    } else if (opt.kind === 'relic' && opt.relic) {
      drawRelicPanel(ctx, r, opt.relic, hovered);
      drawSlotTag(ctx, r, 'RELIC', PALETTE.gold);
    } else if (opt.kind === 'upgrade' && opt.card) {
      // Show the change, not just the result. A card whose name ends in `+`
      // tells the player nothing; "this one costs 1 less" does.
      drawUpgradePanel(ctx, r, opt.base ?? opt.card, opt.card, hovered);
      drawSlotTag(ctx, r, 'UPGRADE', PALETTE.good);
    } else if (opt.kind === 'cut' && opt.cutColor) {
      drawCutPanel(ctx, r, opt.cutColor, hovered);
      drawSlotTag(ctx, r, 'CUT', PALETTE.danger);
    } else if (opt.kind === 'enchant' && opt.enchant) {
      drawEnchantPanel(ctx, r, opt.enchant, hovered);
      drawSlotTag(ctx, r, 'SOCKET', PALETTE.rarityUncommon);
    }
  }

  drawButton(ctx, l.skipButton, 'SKIP  [Esc]', hoverIndex === -1);
}

/** Small banner in a slot's top-right saying what kind of thing it is. */
function drawSlotTag(ctx: CanvasRenderingContext2D, r: Rect, label: string, color: string): void {
  const w = label.length * 6 + 10;
  fillRound(ctx, { x: r.x + r.w - w - 4, y: r.y - 4, w, h: 14 }, 4, 'rgba(13,11,20,0.9)');
  text(ctx, label, r.x + r.w - w / 2 - 4, r.y + 3, 9, color, 'center', MONO);
}

/**
 * A cut slot. Says plainly what it does and, more importantly, what it does
 * NOT do — a "removed" colour still arrives through cards and enemies, which
 * is the whole reason cutting is a build decision rather than a deletion.
 */
function drawCutPanel(ctx: CanvasRenderingContext2D, r: Rect, kind: GemKind, hovered: boolean): void {
  const box = { x: r.x, y: r.y + 8, w: r.w, h: r.h - 16 };
  fillRound(ctx, box, 10, hovered ? '#2a1a1a' : PALETTE.panel);
  strokeRound(ctx, box, 10, PALETTE.danger, hovered ? 2.5 : 1.5);

  const cx = r.x + r.w / 2;
  drawGem(ctx, { x: cx - 22, y: r.y + 44, w: 44, h: 44 }, kind);

  text(ctx, `CUT ${kind.toUpperCase()}`, cx, r.y + 106, 14, GEM_COLOR[kind], 'center', MONO);
  text(ctx, 'The well generates', cx, r.y + 126, 10, PALETTE.dim, 'center', SANS);
  text(ctx, 'less of this colour.', cx, r.y + 139, 10, PALETTE.dim, 'center', SANS);
  text(ctx, 'Cards and enemies', cx, r.y + 158, 9, PALETTE.dim, 'center', SANS);
  text(ctx, 'still introduce it.', cx, r.y + 170, 9, PALETTE.dim, 'center', SANS);
}

/**
 * An enchantment stone on the reward screen. Shows what it does and that it
 * lands on a card — the socket choice comes next, and that choice is the
 * point.
 */
function drawEnchantPanel(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  enchant: EnchantDef,
  hovered: boolean,
): void {
  const box = { x: r.x, y: r.y + 8, w: r.w, h: r.h - 16 };
  fillRound(ctx, box, 10, hovered ? '#1a2030' : PALETTE.panel);
  strokeRound(ctx, box, 10, PALETTE.rarityUncommon, hovered ? 2.5 : 1.5);

  const cx = r.x + r.w / 2;
  drawStoneGem(ctx, { x: cx - 20, y: r.y + 42, w: 40, h: 40 });

  text(ctx, enchant.name.toUpperCase(), cx, r.y + 100, 14, PALETTE.rarityUncommon, 'center', MONO);
  const bodyY = r.y + 120;
  for (const [n, line] of wrap(ctx, enchant.text, r.w - 22, 10).entries()) {
    text(ctx, line, cx, bodyY + n * 13, 10, PALETTE.dim, 'center', SANS);
  }
  text(ctx, 'Socket into a card', cx, r.y + r.h - 30, 9, PALETTE.gold, 'center', MONO);
}

/** The socketing stone, as a glyph distinct from any gem. */
function drawStoneGem(ctx: CanvasRenderingContext2D, r: Rect): void {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const rad = Math.min(r.w, r.h) * 0.42;
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    const px = cx + Math.cos(a) * rad;
    const py = cy + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = PALETTE.rarityUncommon;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}

/**
 * The socket step. The player has taken a stone; now they choose which card
 * gets it. Cards already at their socket capacity are dimmed.
 */
export function drawSocketScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  enchant: EnchantDef,
  hoverIndex: number | null,
): void {
  const deck = run.deck;
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  text(ctx, `SOCKET: ${enchant.name.toUpperCase()}`, cx, l.stage.y + 48, 24, PALETTE.rarityUncommon, 'center', MONO);
  text(ctx, enchant.text, cx, l.stage.y + 76, 12, PALETTE.dim, 'center', SANS);
  text(ctx, 'Choose a card.', cx, l.stage.y + 96, 11, PALETTE.gold, 'center', SANS);

  const rects = socketCardRects(l, deck.length);
  for (let i = 0; i < deck.length; i++) {
    const entry = deck[i];
    const r = rects[i];
    if (!entry || !r) continue;
    const capacity = socketCapacityFor(run, entry.def.id);
    const socketable = canSocket(entry.enchants, capacity);
    const hovered = hoverIndex === i;

    ctx.save();
    if (!socketable) ctx.globalAlpha = 0.4;
    fillRound(ctx, r, 8, hovered && socketable ? '#1a2030' : PALETTE.panel);
    strokeRound(
      ctx,
      r,
      8,
      hovered && socketable ? PALETTE.rarityUncommon : PALETTE.panelEdge,
      hovered && socketable ? 2.5 : 1,
    );
    text(ctx, entry.def.name, r.x + 6, r.y + 14, 10, PALETTE.text, 'left', SANS);
    text(ctx, `${entry.def.cost}`, r.x + r.w - 8, r.y + 14, 11, PALETTE.volt, 'right', MONO);

    // Socket capacity indicator.
    const capLabel = capacity > 1 ? `${entry.enchants.length}/${capacity} sockets` : '';
    if (capLabel) {
      text(ctx, capLabel, r.x + r.w - 8, r.y + 28, 8, PALETTE.gold, 'right', MONO);
    }

    // Existing socketed stones, so the player can see what is already taken.
    let by = r.y + 30;
    for (const e of entry.enchants) {
      const def = enchantById(e);
      text(ctx, `◆ ${def?.name ?? e}`, r.x + 6, by, 9, PALETTE.rarityUncommon, 'left', MONO);
      by += 12;
    }
    if (entry.enchants.length === 0) {
      text(ctx, 'empty socket', r.x + 6, by, 9, PALETTE.dim, 'left', SANS);
    }
    ctx.restore();
  }

  drawButton(ctx, l.skipButton, 'SKIP  [Esc]', hoverIndex === -1);
}

/**
 * Rest/Forge screen. Vertical action cards, one per rest action. Available
 * options are bright; unavailable ones are dimmed. Current HP, deck count,
 * unsocketed enchantment count, and the component inventory are shown below.
 */
export function drawRestScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  text(ctx, 'REST / FORGE', cx, l.stage.y + 48, 24, PALETTE.text, 'center', MONO);
  text(ctx, 'Choose one action. Prepare before you leave.', cx, l.stage.y + 76, 12, PALETTE.dim, 'center', SANS);

  const options = restOptions(run);
  const rects = restOptionRects(l);
  const glyphs = ['\u2665', '\u25C6', '\u2715', '\u2692'];
  const glyphColors = [PALETTE.good, PALETTE.rarityUncommon, PALETTE.danger, PALETTE.gold];

  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    const r = rects[i];
    if (!opt || !r) continue;
    const hovered = hoverIndex === i && opt.available;

    ctx.save();
    if (!opt.available) ctx.globalAlpha = 0.4;
    fillRound(ctx, r, 8, hovered ? '#1e2430' : PALETTE.panel);
    strokeRound(ctx, r, 8, hovered ? PALETTE.gold : PALETTE.panelEdge, hovered ? 2 : 1);

    const glyphColor = glyphColors[i] ?? PALETTE.dim;
    text(ctx, glyphs[i] ?? '', r.x + 28, r.y + r.h / 2, 20, glyphColor, 'center', SANS);
    text(ctx, opt.name, r.x + 52, r.y + r.h / 2 - 10, 14, opt.available ? PALETTE.text : PALETTE.dim, 'left', MONO);
    text(ctx, opt.description, r.x + 52, r.y + r.h / 2 + 12, 11, PALETTE.dim, 'left', SANS);
    text(ctx, String(i + 1), r.x + r.w - 16, r.y + r.h / 2, 12, PALETTE.gold, 'center', MONO);

    ctx.restore();
  }

  const statusY = l.stage.y + l.stage.h - 88;
  const enchants = run.enchantToSocket ? 1 : 0;
  const statusParts = [`HP: ${run.hp}/${run.maxHp}`, `Deck: ${run.deck.length}`];
  if (enchants > 0) statusParts.push(`Enchants: ${enchants} unsocketed`);
  text(ctx, statusParts.join('    '), cx, statusY, 12, PALETTE.dim, 'center', MONO);
  const totals = componentTotalsLine(run.components);
  text(ctx, totals ? `Components: ${totals}` : 'Components: none held', cx, statusY + 16, 10, PALETTE.dim, 'center', MONO);
  text(ctx, `Press 1-${options.length} or click to choose`, cx, statusY + 32, 10, PALETTE.dim, 'center', SANS);
}

/**
 * Purge screen. Shows the deck as a selectable list of card names.
 * Click or press a number to remove a card; Escape returns to rest.
 */
export function drawPurgeScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  text(ctx, 'PURGE A CARD', cx, l.stage.y + 48, 24, PALETTE.danger, 'center', MONO);
  text(ctx, 'Choose a card to remove from your deck', cx, l.stage.y + 76, 12, PALETTE.dim, 'center', SANS);

  const rects = purgeCardRects(l, run.deck.length);
  for (let i = 0; i < run.deck.length; i++) {
    const entry = run.deck[i];
    const r = rects[i];
    if (!entry || !r) continue;
    const hovered = hoverIndex === i;

    ctx.save();
    fillRound(ctx, r, 6, hovered ? '#2a1a1a' : PALETTE.panel);
    strokeRound(ctx, r, 6, hovered ? PALETTE.danger : PALETTE.panelEdge, hovered ? 2 : 1);
    text(ctx, entry.def.name, r.x + 8, r.y + r.h / 2, 11, PALETTE.text, 'left', SANS);
    if (entry.enchants.length > 0) {
      text(ctx, '\u25C6', r.x + r.w - 28, r.y + r.h / 2, 10, PALETTE.rarityUncommon, 'center', SANS);
    }
    text(ctx, String(i + 1), r.x + r.w - 12, r.y + r.h / 2, 9, PALETTE.gold, 'center', MONO);
    ctx.restore();
  }

  text(ctx, 'Escape \u2014 back to rest', cx, l.stage.y + l.stage.h - 40, 11, PALETTE.dim, 'center', MONO);
}

/**
 * Craft screen. Shows every recipe with its component cost and what the run
 * currently holds. Affordable recipes are bright; unaffordable ones dim with
 * their missing components named. Picking one spends the components, stages
 * the resulting gem into `enchantToSocket`, and returns to the rest screen.
 */
export function drawCraftScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  text(ctx, 'CRAFT GEM', cx, l.stage.y + 40, 24, PALETTE.gold, 'center', MONO);
  text(ctx, 'Combine components into a socketable enchantment gem.', cx, l.stage.y + 66, 12, PALETTE.dim, 'center', SANS);

  const rects = craftOptionRects(l, CRAFT_RECIPES.length);
  for (let i = 0; i < CRAFT_RECIPES.length; i++) {
    const recipe = CRAFT_RECIPES[i];
    const r = rects[i];
    if (!recipe || !r) continue;
    const afford = canCraft(run.components, recipe);
    const hovered = hoverIndex === i && afford;

    ctx.save();
    if (!afford) ctx.globalAlpha = 0.45;
    fillRound(ctx, r, 8, hovered ? '#1e2430' : PALETTE.panel);
    strokeRound(ctx, r, 8, hovered ? PALETTE.gold : PALETTE.panelEdge, hovered ? 2 : 1);

    const def = enchantById(recipe.result);
    const enchantName = def?.name ?? recipe.result;
    text(ctx, `\u25C6 ${enchantName}`, r.x + 16, r.y + 17, 14, PALETTE.rarityUncommon, 'left', MONO);

    const costParts: string[] = [];
    const haveParts: string[] = [];
    for (const [comp, n] of Object.entries(recipe.cost)) {
      const id = comp as ComponentId;
      const info = COMPONENTS[id];
      if (!info) continue;
      costParts.push(`${n ?? 0}\u00D7 ${info.abbr}`);
      haveParts.push(`${run.components[id] ?? 0} ${info.abbr}`);
    }
    text(ctx, costParts.join(' + '), r.x + 16, r.y + 36, 11, PALETTE.dim, 'left', MONO);

    const mark = afford ? ' \u2713' : ' \u2715';
    let haveLine = `Have: ${haveParts.join(', ')}${mark}`;
    if (!afford) {
      const missing = missingFor(run.components, recipe);
      const missParts = Object.entries(missing).map(([comp, n]) => `${n ?? 0} ${COMPONENTS[comp as ComponentId]?.abbr ?? comp}`);
      if (missParts.length > 0) haveLine += `  missing ${missParts.join(', ')}`;
    }
    text(ctx, haveLine, r.x + 16, r.y + 54, 11, afford ? PALETTE.good : PALETTE.danger, 'left', SANS);
    text(ctx, String(i + 1), r.x + r.w - 16, r.y + r.h / 2, 12, PALETTE.gold, 'center', MONO);

    ctx.restore();
  }

  const last = rects[rects.length - 1];
  const invY = (last ? last.y + last.h : l.stage.y + l.stage.h * 0.5) + 18;
  const totals = componentTotalsLine(run.components);
  text(ctx, totals ? `Components: ${totals}` : 'Components: none held', cx, invY, 12, PALETTE.dim, 'center', MONO);

  const back = craftBackRect(l);
  drawButton(ctx, back, 'BACK TO REST  [Esc]', hoverIndex === -1);
  text(ctx, 'Crafted gems wait on the rest screen \u2014 Socket to fit one.', cx, back.y - 12, 10, PALETTE.dim, 'center', SANS);
}

const CACHE_GLYPHS: Record<string, string> = {
  socket_expander: '\u25C7',
  rare_card: '\u2663',
  rare_enchant: '\u25C6',
  relic: '\u2726',
};

const CACHE_COLORS: Record<string, string> = {
  socket_expander: PALETTE.rarityUncommon,
  rare_card: PALETTE.rarityRare,
  rare_enchant: PALETTE.gold,
  relic: PALETTE.gold,
};

/**
 * Elite Cache screen. Premium reward options as vertical cards, material
 * reward shown at the bottom. Click or press 1/2/3 to select.
 */
export function drawCacheScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  const options = run.cacheOptions ?? [];

  text(ctx, '\u25C6 ELITE CACHE \u25C6', cx, l.stage.y + 40, 26, PALETTE.gold, 'center', MONO);
  text(ctx, 'Choose one premium reward.', cx, l.stage.y + 68, 13, PALETTE.dim, 'center', SANS);

  const rects = cacheOptionRects(l, options.length);
  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    const r = rects[i];
    if (!opt || !r) continue;
    const hovered = hoverIndex === i;
    const glyph = CACHE_GLYPHS[opt.reward] ?? '\u25C7';
    const color = CACHE_COLORS[opt.reward] ?? PALETTE.gold;

    ctx.save();
    fillRound(ctx, r, 8, hovered ? '#1e2430' : PALETTE.panel);
    strokeRound(ctx, r, 8, hovered ? color : PALETTE.panelEdge, hovered ? 2.5 : 1);

    text(ctx, glyph, r.x + 28, r.y + r.h / 2, 22, color, 'center', SANS);
    text(ctx, opt.name, r.x + 52, r.y + r.h / 2 - 10, 14, PALETTE.text, 'left', MONO);
    text(ctx, opt.description, r.x + 52, r.y + r.h / 2 + 12, 11, PALETTE.dim, 'left', SANS);
    text(ctx, String(i + 1), r.x + r.w - 16, r.y + r.h / 2, 12, PALETTE.gold, 'center', MONO);
    ctx.restore();
  }

  // Material reward shown at the bottom.
  const drops = run.lastRewardDrops;
  if (drops.length > 0) {
    const matY = l.stage.y + l.stage.h * 0.72;
    text(ctx, 'Material reward (automatic):', cx, matY, 12, PALETTE.dim, 'center', SANS);
    const labels = drops.map(d => `${d.count}× ${COMPONENTS[d.id].abbr}`);
    text(ctx, labels.join('  ·  '), cx, matY + 20, 12, PALETTE.text, 'center', MONO);
  }

  const skipY = l.stage.y + l.stage.h - 60;
  text(ctx, 'Press 1/2/3 or click to choose  ·  Esc to skip', cx, skipY, 10, PALETTE.dim, 'center', SANS);
}

/**
 * Socket Expander card selection screen. Shows the deck as a grid of cards
 * with their current socket count. Player picks one card to gain +1 socket.
 */
export function drawSocketExpandScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  run: RunState,
  hoverIndex: number | null,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.95)';
  ctx.fillRect(0, 0, l.width, l.height);

  const cx = l.stage.x + l.stage.w / 2;
  const deck = run.deck;

  text(ctx, 'SOCKET EXPANDER', cx, l.stage.y + 40, 24, PALETTE.rarityUncommon, 'center', MONO);
  text(ctx, 'Choose a card to gain +1 socket (max 2).', cx, l.stage.y + 68, 13, PALETTE.gold, 'center', SANS);

  const rects = socketCardRects(l, deck.length);
  for (let i = 0; i < deck.length; i++) {
    const entry = deck[i];
    const r = rects[i];
    if (!entry || !r) continue;
    const capacity = socketCapacityFor(run, entry.def.id);
    const atMax = capacity >= 2;
    const hovered = hoverIndex === i && !atMax;

    ctx.save();
    if (atMax) ctx.globalAlpha = 0.4;
    fillRound(ctx, r, 8, hovered ? '#1a2030' : PALETTE.panel);
    strokeRound(
      ctx,
      r,
      8,
      hovered ? PALETTE.rarityUncommon : PALETTE.panelEdge,
      hovered ? 2.5 : 1,
    );
    text(ctx, entry.def.name, r.x + 6, r.y + 14, 10, PALETTE.text, 'left', SANS);
    text(ctx, `${entry.def.cost}`, r.x + r.w - 8, r.y + 14, 11, PALETTE.volt, 'right', MONO);

    // Socket count display.
    const socketStr = atMax ? `${capacity} sockets (max)` : `${capacity} → ${capacity + 1} sockets`;
    text(ctx, socketStr, r.x + 6, r.y + 30, 9, atMax ? PALETTE.dim : PALETTE.gold, 'left', MONO);

    // Existing socketed stones.
    let by = r.y + 44;
    for (const e of entry.enchants) {
      const def = enchantById(e);
      text(ctx, `◆ ${def?.name ?? e}`, r.x + 6, by, 9, PALETTE.rarityUncommon, 'left', MONO);
      by += 12;
    }
    if (entry.enchants.length === 0 && !atMax) {
      text(ctx, 'empty socket', r.x + 6, by, 9, PALETTE.dim, 'left', SANS);
    }
    ctx.restore();
  }

  text(ctx, 'Escape \u2014 skip', cx, l.stage.y + l.stage.h - 40, 11, PALETTE.dim, 'center', MONO);
}

/**
 * An upgrade slot shows the change: the card you have, an arrow, the card you
 * get. A `+` suffix on a name is not information; "cost 1 → 0" is.
 */
function drawUpgradePanel(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  base: CardDef,
  upgraded: CardDef,
  hovered: boolean,
): void {
  const box = { x: r.x, y: r.y + 8, w: r.w, h: r.h - 16 };
  fillRound(ctx, box, 10, hovered ? '#1e241c' : PALETTE.panel);
  strokeRound(ctx, box, 10, PALETTE.good, hovered ? 2.5 : 1.5);

  const pad = 8;
  const innerW = box.w - pad * 2;
  const miniW = Math.floor((innerW - 22) / 2);
  const miniH = Math.min(box.h - 52, Math.floor(miniW * 1.45));
  const y = box.y + 30;

  // Left: what you have. Right: what you get. Arrow between.
  drawCard(ctx, { x: box.x + pad, y, w: miniW, h: miniH }, { def: base, uid: -1, enchants: [] }, true, false, false);
  drawCard(ctx, { x: box.x + pad + miniW + 22, y, w: miniW, h: miniH }, { def: upgraded, uid: -2, enchants: [] }, true, false, false);

  const ax = box.x + pad + miniW + 4;
  const ay = y + miniH / 2;
  ctx.save();
  ctx.strokeStyle = PALETTE.good;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(ax + 14, ay);
  ctx.moveTo(ax + 9, ay - 5);
  ctx.lineTo(ax + 15, ay);
  ctx.lineTo(ax + 9, ay + 5);
  ctx.stroke();
  ctx.restore();

  // The one line that actually explains the offer.
  const delta = base.cost - upgraded.cost;
  const label = delta > 0 ? `COST ${base.cost} → ${upgraded.cost}` : 'IMPROVED';
  text(ctx, label, box.x + box.w / 2, box.y + box.h - 16, 11, PALETTE.good, 'center', MONO);
}

function drawRelicPanel(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  relic: RelicDef,
  hovered: boolean,
): void {
  const box = { x: r.x, y: r.y + 30, w: r.w, h: r.h - 60 };
  fillRound(ctx, box, 10, hovered ? '#241c14' : PALETTE.panel);
  strokeRound(ctx, box, 10, PALETTE.gold, hovered ? 2.5 : 1.5);

  const cx = r.x + r.w / 2;
  text(ctx, '◆', cx, r.y + 68, 26, PALETTE.gold, 'center', SANS);
  const nameLines = wrap(ctx, relic.name, r.w - 20, 14);
  for (const [n, line] of nameLines.entries()) {
    text(ctx, line, cx, r.y + 100 + n * 17, 14, PALETTE.text, 'center', SANS);
  }
  const bodyY = r.y + 100 + nameLines.length * 17 + 8;
  for (const [n, line] of wrap(ctx, relic.text, r.w - 24, 11).entries()) {
    text(ctx, line, cx, bodyY + n * 14, 11, PALETTE.dim, 'center', SANS);
  }
}

/** Relic choice screen — same shape as the card reward, different content. */
export function drawEndScreen(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  won: boolean,
  fightsWon: number,
  floorName: string,
  seedCode: string,
  seedCopied: boolean,
  runVersion: string,
  score: RunScore,
  hp: number,
): void {
  ctx.fillStyle = 'rgba(13,11,20,0.9)';
  ctx.fillRect(0, 0, l.width, l.height);
  const cx = l.width / 2;
  const cy = l.height / 2;

  text(
    ctx,
    won ? 'THE MASON FALLS' : 'YOU ARE BURIED',
    cx,
    cy - 110,
    32,
    won ? PALETTE.good : PALETTE.danger,
    'center',
    MONO,
  );
  text(ctx, won ? 'VICTORY' : 'DEFEAT', cx, cy - 82, 16, won ? PALETTE.good : PALETTE.danger, 'center', MONO);

  let y = cy - 56;
  text(ctx, 'RUN SCORE', cx, y, 13, PALETTE.gold, 'center', MONO);
  y += 14;
  text(ctx, '─────────', cx, y, 10, PALETTE.dim, 'center', MONO);
  y += 13;

  const rows: [string, string, number][] = [
    ['Fights won', String(score.fightsWon), score.fightsWon * 100],
    ['Elites', String(score.elitesDefeated), score.elitesDefeated * 250],
    ['Boss', score.bossDefeated ? '\u2713' : '\u2717', score.bossDefeated ? 500 : 0],
    ['HP remaining', String(hp), hp * 2],
    ['Large matches', String(score.largeMatches), score.largeMatches * 50],
    ['Deep cascades', String(score.deepCascades), score.deepCascades * 75],
  ];
  if (score.perfectFights > 0) {
    rows.push(['Perfect fights', String(score.perfectFights), score.perfectFights * 100]);
  }

  const labelX = cx - 110;
  const countX = cx + 16;
  const resultX = cx + 100;
  for (const [label, count, pts] of rows) {
    text(ctx, label, labelX, y, 10, PALETTE.text, 'left', MONO);
    text(ctx, count, countX, y, 10, PALETTE.text, 'right', MONO);
    text(ctx, `= ${pts.toLocaleString()}`, resultX, y, 10, PALETTE.dim, 'right', MONO);
    y += 13;
  }

  y += 2;
  text(ctx, '─────────', cx, y, 10, PALETTE.dim, 'center', MONO);
  y += 14;
  text(ctx, `TOTAL:  ${score.totalScore.toLocaleString()}`, cx, y, 13, PALETTE.gold, 'center', MONO);
  y += 18;
  text(ctx, `Permanent Points Earned:  ${score.permanentPoints}`, cx, y, 11, PALETTE.gold, 'center', MONO);

  y += 22;
  if (won) {
    text(ctx, `Six descents. ${fightsWon} fights won.`, cx, y, 11, PALETTE.dim, 'center', SANS);
  } else {
    text(ctx, `Fallen at ${floorName}. ${fightsWon} fights won.`, cx, y, 11, PALETTE.dim, 'center', SANS);
  }

  y += 18;
  text(ctx, 'Enter — new seed      R — retry this seed', cx, y, 11, PALETTE.gold, 'center', MONO);
  drawSeedBadge(ctx, l, seedCode, seedCopied);
  text(ctx, `ruleset ${runVersion}   ·   click the seed to copy`, cx, y + 20, 9, PALETTE.dim, 'center', SANS);
}

/**
 * The run's identity, on screen. A seed you cannot see is not a seed — it is
 * just a number the game consumed.
 */
export function drawSeedBadge(
  ctx: CanvasRenderingContext2D,
  l: Layout,
  code: string,
  copied: boolean,
): void {
  const r = seedBadgeRect(l);
  fillRound(ctx, r, 8, 'rgba(0,0,0,0.5)');
  strokeRound(ctx, r, 8, copied ? PALETTE.good : PALETTE.panelEdge, 1);
  const mm = getMatchMin();
  const parts: string[] = [`SEED  ${code}`];
  if (mm !== 3) parts.push(`MATCH-${mm}`);
  if (!isDefaultBoard()) parts.push(boardLabel());
  const label = copied ? 'COPIED TO CLIPBOARD' : parts.join('  ·  ');
  const isExperiment = mm !== 3 || !isDefaultBoard();
  text(
    ctx,
    label,
    r.x + r.w / 2,
    r.y + r.h / 2,
    14,
    copied ? PALETTE.good : isExperiment ? PALETTE.gold : PALETTE.text,
    'center',
    MONO,
  );
}

/** Floor breadcrumb drawn over a battle. */
export function drawFloorBanner(ctx: CanvasRenderingContext2D, l: Layout, label: string, progress: string): void {
  text(ctx, label, l.well.x, l.well.y - 40, 16, PALETTE.text, 'left', MONO);
  text(ctx, progress, l.well.x + l.well.w, l.well.y - 40, 13, PALETTE.dim, 'right', MONO);
}

export { drawCard };

export { contains };
